import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  AreaSeries,
  CandlestickSeries,
  createChart,
  createTextWatermark,
  HistogramSeries,
  LineSeries,
  PriceScaleMode,
  TickMarkType,
} from "lightweight-charts";
import API from "../services/api";
import CustomSelect from "./CustomSelect";
import { useToast } from "./Toast";
import { connectWS, onMessage, subscribeToPremium } from "../services/wsService";
import {
  CHART_VIEWPORT_RESTORE_MIN_SOURCE_CANDLES,
  clampChartLogicalRange,
  getChartViewportAction,
} from "../utils/chartViewport";
import {
  buildPremiumChartViewKey,
  getPremiumChartSideLabel,
  getRealtimePremiumValue,
  PREMIUM_CHART_SIDE_STORAGE_KEY,
  restorePremiumChartSide,
} from "../utils/premiumChartSide";
import {
  buildOrderOverlayGroupKey,
  getMobileOrderOverlayLabelKeys,
  getPremiumModeTpOverlayPrice,
  parseExplicitOverlayNumber,
} from "../utils/orderOverlayValues";
import { getTradeFinalPhaseTime } from "../utils/tradeTimes";
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Building2,
  ChartCandlestick,
  Clock3,
  Download,
  Eye,
  EyeOff,
  Grid2x2,
  Layers3,
  User,
  Maximize,
  Minimize,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

const DEBUG_CHART = process.env.REACT_APP_DEBUG_LOGS === "true";

function debugChart(...args) {
  if (DEBUG_CHART) {
    console.log(...args);
  }
}

function debugChartWarn(...args) {
  if (DEBUG_CHART) {
    console.warn(...args);
  }
}

function buildChartColors() {
  const s = typeof document !== "undefined" ? getComputedStyle(document.documentElement) : null;
  const v = (prop, fb) => (s ? s.getPropertyValue(prop).trim() : "") || fb;
  return {
    card: v("--chart-card", "#090909"),
    surface: v("--chart-surface", "#121212"),
    input: v("--chart-input", "#0a0a0a"),
    border: v("--chart-border", "#ffffff1c"),
    borderStrong: v("--chart-border-strong", "#ffffff1c"),
    grid: v("--chart-grid", "rgba(255, 255, 255, 0.07)"),
    muted: v("--chart-muted", "#525252"),
    mutedLight: v("--chart-muted-light", "#a4a4a4"),
    fg: v("--chart-fg", "#ffffff"),
    fgDim: v("--chart-fg-dim", "#e4e4e4"),
    blue: "#2563eb",
    blueLight: v("--chart-blue-light", "#60a5fa"),
    fuchsia: "#c026d3",
    emerald: "#10b981",
    emeraldLight: v("--chart-emerald-light", "#86efac"),
    amber: "#f59e0b",
    amberLight: v("--chart-amber-light", "#fef3c7"),
    cyan: "#22d3ee",
    teal: "#0f766e",
    violet: "#a78bfa",
    orange: "#fb923c",
    lime: "#84cc16",
    rose: "#e11d48",
    roseLight: v("--chart-rose-light", "#fda4af"),
    blueTint: v("--chart-blue-tint", "rgba(37, 99, 235, 0.12)"),
    blueBorder: v("--chart-blue-border", "rgba(37, 99, 235, 0.3)"),
    emeraldTint: v("--chart-emerald-tint", "rgba(16, 185, 129, 0.18)"),
    emeraldSoft: "rgba(16, 185, 129, 0.45)",
    roseSoft: "rgba(225, 29, 72, 0.45)",
    amberSoft: "rgba(245, 158, 11, 0.45)",
    overlay: v("--chart-overlay", "rgba(9, 9, 9, 0.9)"),
    overlayBorder: v("--chart-overlay-border", "rgba(255, 255, 255, 0.08)"),
    watermark: v("--chart-watermark", "rgba(255, 255, 255, 0.07)"),
    panelShadow: v("--chart-panel-shadow", "0 12px 30px rgba(0, 0, 0, 0.4)"),
    fetchingShade: v("--chart-fetching-shade", "rgba(0, 0, 0, 0.36)"),
  };
}

const CHART_COLORS = buildChartColors();

function withAlpha(color, alpha) {
  if (typeof color !== "string") return color;
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }
    if (hex.length === 4) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("")
        .slice(0, 6);
    }
    if (hex.length === 8) {
      hex = hex.slice(0, 6);
    }
    if (hex.length !== 6) {
      return color;
    }
    const int = Number.parseInt(hex, 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (color.startsWith("rgb(")) {
    return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
  }
  return color;
}

function buildGridOptions({ showVertical = false, showHorizontal = false } = {}) {
  return {
    vertLines: {
      visible: showVertical,
      color: showVertical ? CHART_COLORS.grid : withAlpha(CHART_COLORS.card, 0),
    },
    horzLines: {
      visible: showHorizontal,
      color: showHorizontal ? CHART_COLORS.grid : withAlpha(CHART_COLORS.card, 0),
    },
  };
}

function addLineSeries(chart, options) {
  return chart.addSeries(LineSeries, options);
}

function addAreaSeries(chart, options) {
  return chart.addSeries(AreaSeries, options);
}

function addHistogramSeries(chart, options) {
  return chart.addSeries(HistogramSeries, options);
}

function addCandlestickSeries(chart, options) {
  return chart.addSeries(CandlestickSeries, options);
}

function createFixedNumberFormatter(fractionDigits = 2) {
  const locale =
    typeof navigator !== "undefined"
      ? navigator.languages?.[0] || navigator.language || "en-US"
      : "en-US";

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "--";
    return formatter.format(numericValue);
  };
}

function isLightThemeActive() {
  return (
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "light"
  );
}

function buildMainAreaSeriesOptions() {
  const isLightTheme = isLightThemeActive();
  const accentColor = isLightTheme ? CHART_COLORS.blue : CHART_COLORS.blueLight;

  return {
    lineColor: accentColor,
    topColor: withAlpha(accentColor, isLightTheme ? 0.22 : 0.42),
    bottomColor: withAlpha(accentColor, isLightTheme ? 0.05 : 0.14),
    lineWidth: isLightTheme ? 2 : 3,
    priceLineVisible: false,
    lastValueVisible: true,
    crosshairMarkerVisible: true,
    priceFormat: { type: "price", precision: 2, minMove: 0.01 },
  };
}

function buildMainTextWatermarkOptions({ isMobile = false } = {}) {
  return {
    horzAlign: "center",
    vertAlign: "center",
    lines: [
      {
        text: "PREMIX AI",
        color: CHART_COLORS.watermark,
        fontSize: isMobile ? 30 : 72,
        fontStyle: "700",
        fontFamily: "Geist, sans-serif",
      },
    ],
  };
}

function buildCrosshairOptions(lineColor = CHART_COLORS.muted, { width, style } = {}) {
  const crosshairLabelBackground = isLightThemeActive() ? "#000000" : "#ffffff";
  const sharedLineOptions = {
    color: lineColor,
    ...(width == null ? {} : { width }),
    ...(style == null ? {} : { style }),
    labelBackgroundColor: crosshairLabelBackground,
  };

  return {
    vertLine: sharedLineOptions,
    horzLine: sharedLineOptions,
  };
}

function roundOverlayPremium(value) {
  return Math.round(value * 100) / 100;
}

function parseOverlayTimestamp(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
}

function buildOrderOverlayVisual(type, isMobile, { emphasizePending = false } = {}) {
  if (type === "sl") {
    return {
      color: CHART_COLORS.roseLight,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelColor: CHART_COLORS.rose,
      axisLabelTextColor: "#ffffff",
      shortLabel: "SL",
      longLabel: "SL",
    };
  }
  if (type === "tp") {
    return {
      color: CHART_COLORS.emeraldLight,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelColor: CHART_COLORS.emerald,
      axisLabelTextColor: "#ffffff",
      shortLabel: "TP",
      longLabel: "TP",
    };
  }
  if (type === "active") {
    return {
      color: CHART_COLORS.blueLight,
      lineWidth: 2,
      lineStyle: 0,
      axisLabelColor: CHART_COLORS.blue,
      axisLabelTextColor: "#ffffff",
      shortLabel: "A",
      longLabel: "ACTIVE",
    };
  }
  return {
    color: CHART_COLORS.amberLight,
    lineWidth: emphasizePending ? 2 : 1,
    lineStyle: emphasizePending ? 0 : 2,
    axisLabelColor: CHART_COLORS.orange,
    axisLabelTextColor: "#ffffff",
    shortLabel: "P",
    longLabel: "PENDING",
  };
}

function buildOrderOverlayLabel(group, isMobile) {
  const entries = [
    ["sl", group.slCount],
    ["tp", group.tpCount],
    ["active", group.activeCount],
    ["pending", group.pendingCount],
  ].filter(([, count]) => count > 0);

  return entries
    .map(([type, count]) => {
      const visual = buildOrderOverlayVisual(type, isMobile);
      return isMobile ? `${visual.shortLabel}${count}` : `${visual.longLabel} x${count}`;
    })
    .join(isMobile ? " · " : " · ");
}

function buildOrderPriceLineOptions(
  group,
  isMobile,
  showLabel,
  { lineVisible = true, emphasizePending = false } = {},
) {
  const visual = buildOrderOverlayVisual(group.primaryType, isMobile, { emphasizePending });
  return {
    price: group.price,
    color: visual.color,
    lineWidth: visual.lineWidth,
    lineStyle: visual.lineStyle,
    axisLabelVisible: showLabel,
    axisLabelColor: visual.axisLabelColor,
    axisLabelTextColor: visual.axisLabelTextColor,
    title: showLabel ? buildOrderOverlayLabel(group, isMobile) : "",
    lineVisible,
  };
}

function getTimeframeLabel(tf) {
  const numericTf = Number(tf) || 1;
  if (numericTf >= 1440) return "1D";
  if (numericTf >= 240) return "4H";
  if (numericTf >= 60) return "1H";
  return `${numericTf}M`;
}

function getOldContractRequestTimeframe(tf) {
  const numericTf = Number(tf) || 1;
  if (numericTf >= 1440) return "1D";
  if (numericTf >= 240) return "4H";
  if (numericTf >= 60) return "1H";
  return `${numericTf}M`;
}

const axisTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const axisSecondFormatter = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const axisDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
});

const axisDateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const INDICATOR_LIBRARY_ITEMS = [
  {
    key: "sma20",
    title: "Simple Moving Average 20",
    shortLabel: "SMA 20",
    category: "technicals",
    type: "Indicators",
    accentKey: "fuchsia",
    description: "Smooths premium action using a 20-period average.",
  },
  {
    key: "sma50",
    title: "Simple Moving Average 50",
    shortLabel: "SMA 50",
    category: "technicals",
    type: "Indicators",
    accentKey: "amber",
    description: "Longer trend average for broader premium direction.",
  },
  {
    key: "ema12",
    title: "Exponential Moving Average 12",
    shortLabel: "EMA 12",
    category: "technicals",
    type: "Indicators",
    accentKey: "emerald",
    description: "Faster moving average for short-term premium shifts.",
  },
  {
    key: "ema26",
    title: "Exponential Moving Average 26",
    shortLabel: "EMA 26",
    category: "technicals",
    type: "Indicators",
    accentKey: "roseLight",
    description: "Slower EMA used for trend and MACD-style analysis.",
  },
  {
    key: "bb",
    title: "Bollinger Bands",
    shortLabel: "Bollinger Bands",
    category: "technicals",
    type: "Indicators",
    accentKey: "violet",
    description: "Upper, middle, and lower volatility envelopes.",
  },
  {
    key: "vwap",
    title: "VWAP",
    shortLabel: "VWAP",
    category: "technicals",
    type: "Indicators",
    accentKey: "orange",
    description: "Session-weighted average premium reference.",
  },
  {
    key: "rsi",
    title: "Relative Strength Index",
    shortLabel: "RSI",
    category: "technicals",
    type: "Indicators",
    accentKey: "blue",
    description: "Momentum oscillator for overbought and oversold conditions.",
  },
  {
    key: "atr",
    title: "Average True Range",
    shortLabel: "ATR",
    category: "technicals",
    type: "Indicators",
    accentKey: "lime",
    description: "Measures premium volatility over a lookback window.",
  },
  {
    key: "stoch",
    title: "Stochastic Oscillator",
    shortLabel: "Stochastic",
    category: "technicals",
    type: "Indicators",
    accentKey: "cyan",
    description: "Tracks closing position relative to recent range.",
  },
  {
    key: "macd",
    title: "MACD",
    shortLabel: "MACD",
    category: "technicals",
    type: "Indicators",
    accentKey: "rose",
    description: "Trend and momentum combination built from moving averages.",
  },
];

const INDICATOR_SETTINGS_STORAGE_KEY = "sell-premium-chart-indicator-settings:v1";
const CHART_STYLE_STORAGE_KEY = "sell-premium-chart-style:v1";
const ORDERS_OVERLAY_STORAGE_KEY = "sell-premium-chart-orders-overlay:v1";
const REPLAY_SPEED_OPTIONS = [1, 2, 4, 8];
const LIVE_PREMIUM_PAGE_SIZE = 1000;
const OLD_CONTRACTS_MAX_PAGE_SIZE = 1000;
const MAX_ORDER_LABELS_MOBILE = 5;
const SMA20_FUCHSIA = "#c026d3";
const MACD_SIGNAL_TEAL = "#0f766e";
const ORDER_OVERLAY_PRIORITY = {
  sl: 0,
  tp: 1,
  active: 2,
  pending: 3,
};
const VIEWPORT_MODE_RESET = "reset";
const VIEWPORT_MODE_SMART_PRESERVE = "smart-preserve";

function createDefaultIndicatorSettings() {
  return {
    sma20: { enabled: false, period: "20", color: SMA20_FUCHSIA, lineWidth: 2 },
    sma50: { enabled: false, period: "50", color: CHART_COLORS.amber, lineWidth: 2 },
    ema12: { enabled: false, period: "12", color: CHART_COLORS.emerald, lineWidth: 1 },
    ema26: { enabled: false, period: "26", color: CHART_COLORS.roseLight, lineWidth: 1 },
    bb: { enabled: false, period: "20", stdDev: "2", color: CHART_COLORS.violet, lineWidth: 1 },
    vwap: { enabled: false, color: CHART_COLORS.orange, lineWidth: 2 },
    rsi: { enabled: false, period: "14", color: CHART_COLORS.blue, lineWidth: 2 },
    atr: { enabled: false, period: "14", color: CHART_COLORS.lime, lineWidth: 2 },
    stoch: {
      enabled: false,
      kPeriod: "14",
      dPeriod: "3",
      colorK: CHART_COLORS.cyan,
      colorD: CHART_COLORS.amberLight,
      lineWidth: 2,
    },
    macd: {
      enabled: false,
      fast: "12",
      slow: "26",
      signal: "9",
      color: CHART_COLORS.rose,
      signalColor: MACD_SIGNAL_TEAL,
      histUpColor: CHART_COLORS.emeraldSoft,
      histDownColor: CHART_COLORS.roseSoft,
      lineWidth: 2,
    },
  };
}

function applyIndicatorColorMigrations(settings) {
  if (!settings || typeof settings !== "object") return settings;

  return {
    ...settings,
    sma20: settings.sma20
      ? {
          ...settings.sma20,
          color:
            !settings.sma20.color || settings.sma20.color === CHART_COLORS.blueLight
              ? SMA20_FUCHSIA
              : settings.sma20.color,
        }
      : settings.sma20,
    macd: settings.macd
      ? {
          ...settings.macd,
          signalColor:
            !settings.macd.signalColor || settings.macd.signalColor === CHART_COLORS.blueLight
              ? MACD_SIGNAL_TEAL
              : settings.macd.signalColor,
        }
      : settings.macd,
  };
}

function restoreIndicatorSettings() {
  const defaults = createDefaultIndicatorSettings();
  if (typeof window === "undefined") return defaults;

  try {
    const rawValue = window.localStorage.getItem(INDICATOR_SETTINGS_STORAGE_KEY);
    if (!rawValue) return defaults;

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") return defaults;

    const restored = Object.keys(defaults).reduce((next, key) => {
      const storedValue = parsed[key];
      const defaultValue = defaults[key];

      if (!storedValue || typeof storedValue !== "object") {
        next[key] = defaultValue;
        return next;
      }

      next[key] = {
        ...defaultValue,
        ...storedValue,
        enabled: Boolean(storedValue.enabled),
      };
      return next;
    }, {});

    return applyIndicatorColorMigrations(restored);
  } catch (error) {
    return defaults;
  }
}

function restoreChartStyle() {
  if (typeof window === "undefined") return "candles";

  try {
    const rawValue = window.localStorage.getItem(CHART_STYLE_STORAGE_KEY);
    return rawValue === "area" ? "area" : "candles";
  } catch (error) {
    return "candles";
  }
}

function restoreOrdersOverlayEnabled() {
  if (typeof window === "undefined") return true;

  try {
    const rawValue = window.localStorage.getItem(ORDERS_OVERLAY_STORAGE_KEY);
    return rawValue !== "false";
  } catch (error) {
    return true;
  }
}

function sanitizeExportSegment(value, fallback = "chart") {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || fallback;
}

function downloadCanvasAsPng(canvas, filename) {
  return new Promise((resolve, reject) => {
    if (!canvas) {
      reject(new Error("Missing canvas for export."));
      return;
    }

    const triggerDownload = (url) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
    };

    if (typeof canvas.toBlob === "function") {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Failed to generate image blob."));
          return;
        }

        const url = URL.createObjectURL(blob);
        triggerDownload(url);
        setTimeout(() => URL.revokeObjectURL(url), 0);
        resolve();
      }, "image/png");
      return;
    }

    try {
      triggerDownload(canvas.toDataURL("image/png"));
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

export default function SellPremiumChart({
  timeframe = 1,
  company,
  accountSetId,
  currentSet,
  activeTrades = [],
  pendingOrders = [],
  debug = false,
  daysToFetch = null,
  maxPoints = null,
}) {
  const formatPremiumValue = useMemo(() => createFixedNumberFormatter(2), []);
  const [chartStyle, setChartStyle] = useState(() => restoreChartStyle());
  const [premiumChartSide, setPremiumChartSide] = useState(() => restorePremiumChartSide());
  const [ordersOverlayEnabled, setOrdersOverlayEnabled] = useState(() =>
    restoreOrdersOverlayEnabled(),
  );
  const [showGrid, setShowGrid] = useState(true);
  const [replayActive, setReplayActive] = useState(false);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replayCursor, setReplayCursor] = useState(1);
  const [replaySpeed, setReplaySpeed] = useState(2);
  const [candles, setCandles] = useState([]);
  const [latestCandle, setLatestCandle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");
  const [tf, setTf] = useState(timeframe);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenFrame, setFullscreenFrame] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [tpPausedMeta, setTpPausedMeta] = useState(null);
  const toast = useToast();

  const [indicatorSettings, setIndicatorSettings] = useState(() => restoreIndicatorSettings());

  useEffect(() => {
    setIndicatorSettings((previous) => {
      const next = applyIndicatorColorMigrations(previous);
      if (
        next?.sma20?.color === previous?.sma20?.color &&
        next?.macd?.signalColor === previous?.macd?.signalColor
      ) {
        return previous;
      }
      return next;
    });
  }, []);

  const chartContainer = useRef();
  const chartRef = useRef();
  const seriesRef = useRef();
  const candleSeriesRef = useRef();
  const areaSeriesRef = useRef();
  const indicatorSeriesRefs = useRef({});
  const indicatorValueMapRef = useRef({});
  const rsiContainerRef = useRef();
  const macdContainerRef = useRef();
  const atrContainerRef = useRef();
  const stochContainerRef = useRef();
  const rsiChartRef = useRef();
  const macdChartRef = useRef();
  const atrChartRef = useRef();
  const stochChartRef = useRef();
  const rsiSeriesRef = useRef();
  const macdSeriesRefs = useRef({ macd: null, signal: null, hist: null });
  const atrSeriesRef = useRef();
  const stochSeriesRefs = useRef({ k: null, d: null });
  const textWatermarkRef = useRef(null);
  const orderPriceLinesRef = useRef([]);
  const [themeTick, setThemeTick] = useState(0);

  useEffect(() => {
    const syncTheme = () => {
      Object.assign(CHART_COLORS, buildChartColors());
      setThemeTick((t) => t + 1);

      const chartLayoutOpts = {
        layout: { background: { color: CHART_COLORS.card }, textColor: CHART_COLORS.fgDim },
        grid: buildGridOptions({ showVertical: false, showHorizontal: showGrid }),
        rightPriceScale: { borderColor: CHART_COLORS.borderStrong },
        leftPriceScale: { borderColor: CHART_COLORS.borderStrong },
        crosshair: buildCrosshairOptions(CHART_COLORS.muted),
        timeScale: { borderColor: CHART_COLORS.borderStrong },
        localization: { priceFormatter: formatPremiumValue },
      };
      const subChartOpts = {
        ...chartLayoutOpts,
        layout: { background: { color: CHART_COLORS.card }, textColor: CHART_COLORS.mutedLight },
        grid: buildGridOptions({ showVertical: false, showHorizontal: showGrid }),
      };

      try {
        chartRef.current?.applyOptions(chartLayoutOpts);
      } catch {}
      try {
        rsiChartRef.current?.applyOptions(subChartOpts);
      } catch {}
      try {
        macdChartRef.current?.applyOptions(subChartOpts);
      } catch {}
      try {
        atrChartRef.current?.applyOptions(subChartOpts);
      } catch {}
      try {
        stochChartRef.current?.applyOptions(subChartOpts);
      } catch {}
      try {
        areaSeriesRef.current?.applyOptions(buildMainAreaSeriesOptions());
      } catch {}

      try {
        indicatorSeriesRefs.current.bbFillLower?.applyOptions({
          topColor: CHART_COLORS.card,
          bottomColor: CHART_COLORS.card,
        });
      } catch {}
    };

    syncTheme();

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === "data-theme") {
          requestAnimationFrame(syncTheme);
          break;
        }
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, [formatPremiumValue, showGrid]);

  const rsiPriceLinesRef = useRef({ upper: null, lower: null });
  const stochPriceLinesRef = useRef({ upper: null, lower: null });
  const indicatorPanelRef = useRef(null);
  const modeToggleRef = useRef(null);
  const fullscreenRootRef = useRef(null);
  const fullscreenScrollSnapshotRef = useRef(null);
  const restoreScrollOnFullscreenExitRef = useRef(false);
  const initialLoaded = useRef(false);
  const wsHistoryRecoveryAttemptRef = useRef(false);
  const wsHistoryRecoveryTimeoutRef = useRef(null);
  const lastCandleTimeRef = useRef(null);
  const candlesRef = useRef([]);
  const candleMapRef = useRef(new Map());
  const fetchRequestIdRef = useRef(0);
  const isFetchingOlderRef = useRef(false);
  const hasMoreDataRef = useRef(true);
  const oldestCandleTimeRef = useRef(null);
  const oldContractsNextPageRef = useRef(1);
  const oldContractsPageSizeRef = useRef(OLD_CONTRACTS_MAX_PAGE_SIZE);
  const historyLoadArmedRef = useRef(false);
  const replayActiveRef = useRef(false);
  const chartControlsHideTimeoutRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const visibleLogicalRangeRef = useRef(null);
  const resetViewportOnNextLoadRef = useRef(true);
  const pendingViewportActionRef = useRef(null);
  const lastLoadedChartViewKeyRef = useRef(null);
  const autoFollowRef = useRef(true);
  const programmaticRangeChangeRef = useRef(false);
  const programmaticCrosshairSyncRef = useRef(false);

  const [, setLastUpdate] = useState(null);
  const [, setCurrentCandleHighlight] = useState(false);
  const [, setCurrentPrice] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [rsiReady, setRsiReady] = useState(false);
  const [macdReady, setMacdReady] = useState(false);
  const [atrReady, setAtrReady] = useState(false);
  const [stochReady, setStochReady] = useState(false);
  const [chartControlsVisible, setChartControlsVisible] = useState(false);
  const [indicatorPanelOpen, setIndicatorPanelOpen] = useState(false);
  const [selectedIndicatorKey, setSelectedIndicatorKey] = useState("sma20");
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1200 : window.innerWidth,
  );
  const [mobileTopControlWidth, setMobileTopControlWidth] = useState(0);
  const [sharedRightScaleMinWidth, setSharedRightScaleMinWidth] = useState(0);

  const [isOldContractsMode, setIsOldContractsMode] = useState(false);
  const [oldContractsSymbols, setOldContractsSymbols] = useState([]);
  const [oldContractPairs, setOldContractPairs] = useState([]);
  const [selectedOldSymbol, setSelectedOldSymbol] = useState("");
  const [oldContractsSymbolsLoaded, setOldContractsSymbolsLoaded] = useState(false);
  const [liveClock, setLiveClock] = useState(() => new Date());
  const lastErrorToastRef = useRef("");
  const lastTpPausedToastRef = useRef("");
  const isMobileViewport = viewportWidth <= 768;
  const isCompactViewport = viewportWidth <= 980;
  const isCompactToolbarViewport = viewportWidth <= 1180;
  const CHART_CONTROLS_MOBILE_HIDE_DELAY = 1800;

  const clearChartControlsHideTimeout = useCallback(() => {
    if (chartControlsHideTimeoutRef.current) {
      window.clearTimeout(chartControlsHideTimeoutRef.current);
      chartControlsHideTimeoutRef.current = null;
    }
  }, []);

  const showChartControls = useCallback(
    ({ autoHideMs = null } = {}) => {
      setChartControlsVisible(true);
      clearChartControlsHideTimeout();
      if (autoHideMs) {
        chartControlsHideTimeoutRef.current = window.setTimeout(() => {
          setChartControlsVisible(false);
          chartControlsHideTimeoutRef.current = null;
        }, autoHideMs);
      }
    },
    [clearChartControlsHideTimeout],
  );

  const hideChartControls = useCallback(() => {
    clearChartControlsHideTimeout();
    setChartControlsVisible(false);
  }, [clearChartControlsHideTimeout]);

  const measureFullscreenFrame = useCallback(() => {
    if (typeof window === "undefined") return null;

    const fallbackWidth = Math.round(
      window.innerWidth || document.documentElement?.clientWidth || 0,
    );
    const fallbackHeight = Math.round(
      window.innerHeight || document.documentElement?.clientHeight || 0,
    );
    const shell =
      fullscreenRootRef.current?.closest?.(".app-shell-private") ||
      document.querySelector(".app-shell-private");

    if (!shell) {
      return {
        top: 0,
        left: 0,
        width: fallbackWidth,
        height: fallbackHeight,
        borderRadius: "0px",
      };
    }

    const rect = shell.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(shell);

    return {
      top: Math.round(rect.top),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
      borderRadius: computedStyle.borderRadius || "0px",
    };
  }, []);

  const captureFullscreenScrollSnapshot = useCallback(() => {
    if (typeof window === "undefined") return null;

    const shell =
      fullscreenRootRef.current?.closest?.(".app-shell-private") ||
      document.querySelector(".app-shell-private");

    return {
      windowX: window.scrollX,
      windowY: window.scrollY,
      shellScrollLeft: shell?.scrollLeft ?? null,
      shellScrollTop: shell?.scrollTop ?? null,
    };
  }, []);

  const exitFullscreen = useCallback(() => {
    restoreScrollOnFullscreenExitRef.current = true;
    setFullscreenFrame(null);
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
      return;
    }

    fullscreenScrollSnapshotRef.current = captureFullscreenScrollSnapshot();
    setFullscreenFrame(measureFullscreenFrame());
    setIsFullscreen(true);
  }, [captureFullscreenScrollSnapshot, exitFullscreen, isFullscreen, measureFullscreenFrame]);

  const oldContractTimeframe = getOldContractRequestTimeframe(tf);
  const oldContractsLocked = Boolean(
    currentSet?.symbolsLocked && currentSet?.futureSymbol && currentSet?.spotSymbol,
  );
  const canLoadOldContracts = Boolean(
    accountSetId && oldContractsLocked && currentSet?.premiumTableName,
  );
  const oldContractsHelperText = !accountSetId
    ? "Select an account set to view old contract history."
    : !oldContractsLocked || !currentSet?.premiumTableName
      ? "Old contract history becomes available after the current symbol pair is locked for this account set."
      : "";
  const oldContractsSelectDisabled = !canLoadOldContracts || oldContractsSymbols.length === 0;
  const oldContractsPlaceholder = !canLoadOldContracts
    ? "Old history unavailable"
    : oldContractsSymbolsLoaded && oldContractsSymbols.length === 0
      ? "No old symbols found"
      : "Select Symbol";
  const replayVisibleCount = replayActive
    ? Math.min(Math.max(replayCursor, 1), candles.length || 1)
    : candles.length;
  const chartCandles = useMemo(
    () => (replayActive ? candles.slice(0, replayVisibleCount) : candles),
    [candles, replayActive, replayVisibleCount],
  );
  const hasChartData = chartCandles.length > 0;
  const replayVisibleUntilTime = chartCandles[chartCandles.length - 1]?.time ?? null;
  const currentOverlayReferencePrice = chartCandles[chartCandles.length - 1]?.close ?? null;
  const stableOverlaysRef = useRef([]);
  const stableOverlaysJsonRef = useRef("[]");
  const mobileLabelSelectionRef = useRef({ overlayJson: "", keys: [] });

  const normalizedOrderOverlays = useMemo(() => {
    if (isOldContractsMode || !hasChartData) return [];

    const futureSymbol = currentSet?.futureSymbol || "";
    const spotSymbol = currentSet?.spotSymbol || "";
    const overlays = [];

    const isReplayVisible = (timeValue) => {
      if (!replayActive || replayVisibleUntilTime == null) return true;
      const normalizedTime = parseOverlayTimestamp(timeValue);
      if (normalizedTime == null) return true;
      return normalizedTime <= replayVisibleUntilTime;
    };

    activeTrades.forEach((trade) => {
      if (trade?.accountSetId && String(trade.accountSetId) !== String(accountSetId)) return;
      if (futureSymbol && trade?.broker1Symbol && trade.broker1Symbol !== futureSymbol) return;
      if (spotSymbol && trade?.broker2Symbol && trade.broker2Symbol !== spotSymbol) return;
      if (!isReplayVisible(getTradeFinalPhaseTime(trade, "open"))) return;

      const openingPremium =
        Number.isFinite(Number(trade?.broker1OpenPrice)) &&
        Number.isFinite(Number(trade?.broker2OpenPrice))
          ? Number(trade.broker1OpenPrice) - Number(trade.broker2OpenPrice)
          : Number(trade?.executionPremium);
      if (Number.isFinite(openingPremium)) {
        overlays.push({
          type: "active",
          price: roundOverlayPremium(openingPremium),
        });
      }

      const activeTradeTakeProfitPrice = getPremiumModeTpOverlayPrice(
        openingPremium,
        trade?.takeProfit,
        trade?.takeProfitMode,
      );
      if (activeTradeTakeProfitPrice != null) {
        overlays.push({
          type: "tp",
          price: roundOverlayPremium(activeTradeTakeProfitPrice),
        });
      }

      const activeTradeStopLoss = parseExplicitOverlayNumber(trade?.stopLoss);
      if (activeTradeStopLoss != null) {
        overlays.push({
          type: "sl",
          price: roundOverlayPremium(activeTradeStopLoss),
        });
      }
    });

    pendingOrders.forEach((order) => {
      if (order?.accountSetId && String(order.accountSetId) !== String(accountSetId)) return;
      if (!["Pending", "Executing"].includes(String(order?.status || ""))) return;
      if (!isReplayVisible(order?.createdAt)) return;

      const pendingTargetPremium = parseExplicitOverlayNumber(order?.targetPremium);
      if (pendingTargetPremium != null) {
        overlays.push({
          type: "pending",
          price: roundOverlayPremium(pendingTargetPremium),
        });
      }

      const pendingTakeProfitPrice = getPremiumModeTpOverlayPrice(
        pendingTargetPremium,
        order?.takeProfit,
        order?.takeProfitMode,
      );
      if (pendingTakeProfitPrice != null) {
        overlays.push({
          type: "tp",
          price: roundOverlayPremium(pendingTakeProfitPrice),
        });
      }

      const pendingStopLoss = parseExplicitOverlayNumber(order?.stopLoss);
      if (pendingStopLoss != null) {
        overlays.push({
          type: "sl",
          price: roundOverlayPremium(pendingStopLoss),
        });
      }
    });

    const grouped = new Map();
    overlays.forEach((entry) => {
      if (!Number.isFinite(entry.price)) return;
      const key = entry.price.toFixed(2);
      const current = grouped.get(key) || {
        price: entry.price,
        activeCount: 0,
        pendingCount: 0,
        tpCount: 0,
        slCount: 0,
        primaryType: entry.type,
      };

      if (entry.type === "active") current.activeCount += 1;
      if (entry.type === "pending") current.pendingCount += 1;
      if (entry.type === "tp") current.tpCount += 1;
      if (entry.type === "sl") current.slCount += 1;

      if (ORDER_OVERLAY_PRIORITY[entry.type] < ORDER_OVERLAY_PRIORITY[current.primaryType]) {
        current.primaryType = entry.type;
      }

      grouped.set(key, current);
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const typeDiff =
        ORDER_OVERLAY_PRIORITY[a.primaryType] - ORDER_OVERLAY_PRIORITY[b.primaryType];
      if (typeDiff !== 0) return typeDiff;
      return b.price - a.price;
    });
  }, [
    accountSetId,
    activeTrades,
    currentSet?.futureSymbol,
    currentSet?.spotSymbol,
    hasChartData,
    isOldContractsMode,
    pendingOrders,
    replayActive,
    replayVisibleUntilTime,
  ]);

  const stableOrderOverlays = useMemo(() => {
    const json = JSON.stringify(normalizedOrderOverlays);
    if (json === stableOverlaysJsonRef.current) return stableOverlaysRef.current;
    stableOverlaysJsonRef.current = json;
    stableOverlaysRef.current = normalizedOrderOverlays;
    return normalizedOrderOverlays;
  }, [normalizedOrderOverlays]);

  const mobileOrderOverlayLabelKeys = useMemo(() => {
    const overlayJson = stableOverlaysJsonRef.current;
    if (mobileLabelSelectionRef.current.overlayJson === overlayJson) {
      return mobileLabelSelectionRef.current.keys;
    }

    const nextKeys = getMobileOrderOverlayLabelKeys(
      stableOrderOverlays,
      currentOverlayReferencePrice,
      MAX_ORDER_LABELS_MOBILE,
    );

    mobileLabelSelectionRef.current = {
      overlayJson,
      keys: nextKeys,
    };

    return nextKeys;
  }, [currentOverlayReferencePrice, stableOrderOverlays]);

  const chartBarSpacing = isMobileViewport
    ? 10
    : Math.max(12, Math.min(18, Math.round(viewportWidth / 90)));
  const tpPausedNotice = useMemo(() => {
    if (!tpPausedMeta || tpPausedMeta.status !== "paused") return "";
    return "Live prices are paused right now. Please wait for fresh quotes.";
  }, [tpPausedMeta]);

  useEffect(() => {
    candlesRef.current = candles;
    candleMapRef.current = new Map(candles.map((candle) => [candle.time, candle]));
  }, [candles]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setLiveClock(new Date());
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [formatPremiumValue]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    return () => {
      clearChartControlsHideTimeout();
    };
  }, [clearChartControlsHideTimeout]);

  useEffect(() => {
    clearChartControlsHideTimeout();
    setChartControlsVisible(false);
  }, [isMobileViewport, clearChartControlsHideTimeout]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const nextWidth = Math.ceil(modeToggleRef.current?.getBoundingClientRect().width || 0);
      if (nextWidth > 0) {
        setMobileTopControlWidth((previous) => (previous === nextWidth ? previous : nextWidth));
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isMobileViewport, viewportWidth]);

  useEffect(() => {
    if (!error) return;
    if (lastErrorToastRef.current === error) return;
    lastErrorToastRef.current = error;
    toast.error(error);
  }, [error, toast]);

  useEffect(() => {
    if (!tpPausedNotice) {
      lastTpPausedToastRef.current = "";
      return;
    }
    if (lastTpPausedToastRef.current === tpPausedNotice) return;
    if (typeof window !== "undefined") {
      const lastAt = Number(window.__premiumProfitPassiveMarketPauseToastAt || 0);
      const nowTs = Date.now();
      if (nowTs - lastAt < 3000) {
        lastTpPausedToastRef.current = tpPausedNotice;
        return;
      }
      window.__premiumProfitPassiveMarketPauseToastAt = nowTs;
    }
    lastTpPausedToastRef.current = tpPausedNotice;
    toast.warning(tpPausedNotice);
  }, [toast, tpPausedNotice]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        INDICATOR_SETTINGS_STORAGE_KEY,
        JSON.stringify(indicatorSettings),
      );
    } catch (error) {}
  }, [indicatorSettings]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(CHART_STYLE_STORAGE_KEY, chartStyle);
    } catch (error) {}
  }, [chartStyle]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(PREMIUM_CHART_SIDE_STORAGE_KEY, premiumChartSide);
    } catch (error) {}
  }, [premiumChartSide]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(ORDERS_OVERLAY_STORAGE_KEY, String(ordersOverlayEnabled));
    } catch (error) {}
  }, [ordersOverlayEnabled]);

  useEffect(() => {
    replayActiveRef.current = replayActive;
  }, [replayActive]);

  useEffect(() => {
    if (!replayActive) return;

    if (candles.length === 0) {
      setReplayPlaying(false);
      setReplayActive(false);
      return;
    }

    setReplayCursor((previous) => Math.min(Math.max(previous, 1), candles.length));
  }, [candles.length, replayActive]);

  useEffect(() => {
    if (!replayActive || !replayPlaying || candles.length === 0) return;

    const frameDelay = Math.max(90, Math.round(900 / replaySpeed));
    const timerId = window.setInterval(() => {
      setReplayCursor((previous) => {
        if (previous >= candles.length) {
          setReplayPlaying(false);
          return previous;
        }
        return previous + 1;
      });
    }, frameDelay);

    return () => {
      window.clearInterval(timerId);
    };
  }, [candles.length, replayActive, replayPlaying, replaySpeed]);

  const isDisposedChartError = useCallback((error) => {
    const message = error?.message ?? String(error ?? "");
    return /disposed/i.test(message);
  }, []);

  const safelyRunChartOperation = useCallback(
    (operation) => {
      try {
        return operation?.();
      } catch (error) {
        if (isDisposedChartError(error)) {
          return undefined;
        }
        throw error;
      }
    },
    [isDisposedChartError],
  );

  const clearOrderPriceLines = useCallback(() => {
    orderPriceLinesRef.current.forEach(({ line, series }) => {
      safelyRunChartOperation(() => {
        series?.removePriceLine?.(line);
      });
    });
    orderPriceLinesRef.current = [];
  }, [safelyRunChartOperation]);

  const toAreaSeriesData = useCallback(
    (candleData) =>
      Array.isArray(candleData)
        ? candleData
            .filter((item) => Number.isFinite(item?.time) && Number.isFinite(item?.close))
            .map((item) => ({
              time: item.time,
              value: item.close,
            }))
        : [],
    [],
  );

  const attachChartResizeObserver = useCallback(
    (container, chart) => {
      if (!container || !chart || typeof ResizeObserver === "undefined") {
        return () => {};
      }

      let disposed = false;
      let frameId = 0;
      let lastWidth = -1;
      let lastHeight = -1;

      const applyChartSize = (width, height) => {
        if (disposed) return;

        const nextWidth = Math.round(width);
        const nextHeight = Math.round(height);
        if (nextWidth <= 0 || nextHeight <= 0) return;
        if (nextWidth === lastWidth && nextHeight === lastHeight) return;

        lastWidth = nextWidth;
        lastHeight = nextHeight;

        safelyRunChartOperation(() => {
          chart.applyOptions({
            width: nextWidth,
            height: nextHeight,
            timeScale: {
              barSpacing: Math.max(10, Math.min(20, Math.round(nextWidth / 90))),
              minBarSpacing: Math.max(8, Math.min(18, Math.round(nextWidth / 100))),
            },
          });
        });
      };

      const scheduleChartSize = (width, height) => {
        if (disposed) return;
        if (frameId) {
          window.cancelAnimationFrame(frameId);
        }
        frameId = window.requestAnimationFrame(() => {
          frameId = 0;
          applyChartSize(width, height);
        });
      };

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry || disposed) return;
        scheduleChartSize(entry.contentRect.width, entry.contentRect.height);
      });

      observer.observe(container);
      scheduleChartSize(container.clientWidth, container.clientHeight);

      return () => {
        disposed = true;
        if (frameId) {
          window.cancelAnimationFrame(frameId);
        }
        observer.disconnect();
      };
    },
    [safelyRunChartOperation],
  );

  const resetAllPriceScalesToAuto = useCallback(() => {
    safelyRunChartOperation(() => {
      chartRef.current?.applyOptions({
        rightPriceScale: {
          autoScale: true,
          mode: PriceScaleMode.Normal,
          minimumWidth: sharedRightScaleMinWidth,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1,
          },
        },
        leftPriceScale: {
          autoScale: false,
          borderColor: CHART_COLORS.borderStrong,
          visible: false,
        },
      });
    });

    [rsiChartRef.current, macdChartRef.current, atrChartRef.current, stochChartRef.current].forEach(
      (chartInstance) => {
        safelyRunChartOperation(() => {
          chartInstance?.applyOptions({
            rightPriceScale: {
              autoScale: true,
              mode: PriceScaleMode.Normal,
              minimumWidth: sharedRightScaleMinWidth,
            },
          });
        });
      },
    );
  }, [safelyRunChartOperation, sharedRightScaleMinWidth]);

  useEffect(() => {
    const getVisibleCharts = () =>
      [
        { chart: chartRef.current, container: chartContainer.current, enabled: true },
        {
          chart: rsiChartRef.current,
          container: rsiContainerRef.current,
          enabled: indicatorSettings.rsi.enabled && rsiReady,
        },
        {
          chart: macdChartRef.current,
          container: macdContainerRef.current,
          enabled: indicatorSettings.macd.enabled && macdReady,
        },
        {
          chart: atrChartRef.current,
          container: atrContainerRef.current,
          enabled: indicatorSettings.atr.enabled && atrReady,
        },
        {
          chart: stochChartRef.current,
          container: stochContainerRef.current,
          enabled: indicatorSettings.stoch.enabled && stochReady,
        },
      ].filter((item) => item.enabled && item.chart && item.container?.isConnected);

    const visibleCharts = getVisibleCharts();

    if (!visibleCharts.length) return;

    visibleCharts.forEach(({ chart }) => {
      safelyRunChartOperation(() => {
        chart.priceScale("right").applyOptions({
          minimumWidth: 0,
        });
      });
    });

    const frameId = window.requestAnimationFrame(() => {
      const widths = getVisibleCharts()
        .map(({ chart }) =>
          safelyRunChartOperation(() => {
            const priceScale = chart.priceScale("right");
            return priceScale?.width?.();
          }),
        )
        .filter((width) => Number.isFinite(width) && width > 0);

      const fallbackWidth = isMobileViewport ? 46 : 54;
      const nextWidth =
        widths.length > 0
          ? Math.max(fallbackWidth, ...widths.map((width) => Math.ceil(width)))
          : fallbackWidth;

      setSharedRightScaleMinWidth((previous) =>
        Math.abs(previous - nextWidth) <= 1 ? previous : nextWidth,
      );
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [
    candles,
    indicatorSettings.atr.enabled,
    indicatorSettings.macd.enabled,
    indicatorSettings.rsi.enabled,
    indicatorSettings.stoch.enabled,
    isMobileViewport,
    rsiReady,
    macdReady,
    atrReady,
    stochReady,
    safelyRunChartOperation,
    viewportWidth,
  ]);

  useEffect(() => {
    const nextWidth = sharedRightScaleMinWidth;
    const applySharedWidth = (chartInstance, container) => {
      if (!chartInstance || !container?.isConnected) return;
      safelyRunChartOperation(() => {
        chartInstance.priceScale("right").applyOptions({
          minimumWidth: nextWidth,
        });
      });
    };

    applySharedWidth(chartRef.current, chartContainer.current);
    applySharedWidth(rsiChartRef.current, rsiContainerRef.current);
    applySharedWidth(macdChartRef.current, macdContainerRef.current);
    applySharedWidth(atrChartRef.current, atrContainerRef.current);
    applySharedWidth(stochChartRef.current, stochContainerRef.current);
  }, [
    sharedRightScaleMinWidth,
    safelyRunChartOperation,
    rsiReady,
    macdReady,
    atrReady,
    stochReady,
  ]);

  const clampLogicalRange = useCallback(
    (range, totalBars) => clampChartLogicalRange(range, totalBars),
    [],
  );

  const runProgrammaticTimeScaleUpdate = useCallback(
    (callback, { enableAutoFollow = false } = {}) => {
      programmaticRangeChangeRef.current = true;
      callback?.();
      if (enableAutoFollow) {
        autoFollowRef.current = true;
      }
      window.requestAnimationFrame(() => {
        programmaticRangeChangeRef.current = false;
      });
    },
    [],
  );

  const applyResetViewport = useCallback(
    (barsCount = candlesRef.current.length, { enableAutoFollow = true } = {}) => {
      if (!chartRef.current) return;

      const timeScale = chartRef.current.timeScale();
      const fullRange =
        barsCount > 0 ? clampLogicalRange({ from: -0.5, to: barsCount - 0.5 }, barsCount) : null;

      resetAllPriceScalesToAuto();

      runProgrammaticTimeScaleUpdate(
        () => {
          if (fullRange) {
            timeScale.setVisibleLogicalRange(fullRange);
          } else {
            timeScale.resetTimeScale();
            timeScale.fitContent();
          }
        },
        { enableAutoFollow },
      );

      window.requestAnimationFrame(() => {
        resetAllPriceScalesToAuto();
        visibleLogicalRangeRef.current = timeScale.getVisibleLogicalRange();
      });

      resetViewportOnNextLoadRef.current = false;
    },
    [clampLogicalRange, resetAllPriceScalesToAuto, runProgrammaticTimeScaleUpdate],
  );

  const applyViewportAction = useCallback(
    (action, barsCount = candlesRef.current.length) => {
      if (!chartRef.current) return;

      if (action?.type === "preserve" && action.logicalRange) {
        runProgrammaticTimeScaleUpdate(() => {
          chartRef.current?.timeScale().setVisibleLogicalRange(action.logicalRange);
        });
        visibleLogicalRangeRef.current = action.logicalRange;
        resetViewportOnNextLoadRef.current = false;
        return;
      }

      applyResetViewport(barsCount);
    },
    [applyResetViewport, runProgrammaticTimeScaleUpdate],
  );

  const timeframes = [
    { value: 1, label: "1M" },
    { value: 5, label: "5M" },
    { value: 15, label: "15M" },
    { value: 30, label: "30M" },
    { value: 60, label: "1H" },
    { value: 240, label: "4H" },
    { value: 1440, label: "1D" },
  ];
  const premiumChartSideLabel = getPremiumChartSideLabel(premiumChartSide);
  const currentChartViewKey = buildPremiumChartViewKey({
    isOldContractsMode,
    selectedOldSymbol,
    timeframe: tf,
    accountSetId,
    side: premiumChartSide,
  });
  useEffect(() => {
    setReplayPlaying(false);
    setReplayActive(false);
    setReplayCursor(1);
  }, [currentChartViewKey]);
  const oldSymbolOptions = useMemo(
    () =>
      oldContractsSymbols.map((symbol) => ({
        value: symbol,
        label: oldContractPairs.find((pair) => pair?.symbol === symbol)?.label || symbol,
      })),
    [oldContractPairs, oldContractsSymbols],
  );
  const footerTimeframeButtons = timeframes.map((item) => ({
    key: String(item.value),
    label: item.label,
    active: tf === item.value,
    onClick: () => {
      setTf(item.value);
      initialLoaded.current = false;
    },
  }));
  const cycleChartStyle = useCallback(() => {
    setChartStyle((previous) => (previous === "candles" ? "area" : "candles"));
  }, []);

  const calculateSMA = useCallback((data, period) => {
    const result = [];
    for (let i = period - 1; i < data.length; i++) {
      const sum = data.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val.close, 0);
      result.push({
        time: data[i].time,
        value: sum / period,
      });
    }
    return result;
  }, []);

  const calculateEMA = useCallback((data, period) => {
    if (data.length === 0) return [];
    const result = [];
    const multiplier = 2 / (period + 1);
    let ema = data[0].close;

    result.push({ time: data[0].time, value: ema });

    for (let i = 1; i < data.length; i++) {
      ema = (data[i].close - ema) * multiplier + ema;
      result.push({
        time: data[i].time,
        value: ema,
      });
    }
    return result;
  }, []);

  const calculateRSI = useCallback((data, period = 14) => {
    if (data.length <= period) return [];

    const result = [];
    let gains = 0;
    let losses = 0;

    for (let i = 1; i <= period; i++) {
      const change = data[i].close - data[i - 1].close;
      if (change > 0) gains += change;
      else losses -= change;
    }

    let avgGain = gains / period;
    let avgLoss = losses / period;

    for (let i = period; i < data.length; i++) {
      const change = data[i].close - data[i - 1].close;
      const gain = change > 0 ? change : 0;
      const loss = change < 0 ? -change : 0;

      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;

      const rs = avgGain / avgLoss;
      const rsi = 100 - 100 / (1 + rs);

      result.push({
        time: data[i].time,
        value: rsi,
      });
    }
    return result;
  }, []);

  const calculateBollingerBands = useCallback(
    (data, period, stdDev) => {
      if (data.length < period) return { upper: [], middle: [], lower: [] };
      const middle = calculateSMA(data, period);
      const upper = [];
      const lower = [];
      for (let i = period - 1; i < data.length; i++) {
        const slice = data.slice(i - period + 1, i + 1).map((c) => c.close);
        const mean =
          middle[i - (period - 1)]?.value ?? slice.reduce((acc, v) => acc + v, 0) / period;
        const variance = slice.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / period;
        const deviation = Math.sqrt(variance) * stdDev;
        const time = data[i].time;
        upper.push({ time, value: mean + deviation });
        lower.push({ time, value: mean - deviation });
      }
      return { upper, middle, lower };
    },
    [calculateSMA],
  );

  const calculateEMAOnSeries = useCallback((seriesData, period) => {
    if (!seriesData.length) return [];
    const result = [];
    const multiplier = 2 / (period + 1);
    let ema = seriesData[0].value;
    result.push({ time: seriesData[0].time, value: ema });
    for (let i = 1; i < seriesData.length; i++) {
      ema = (seriesData[i].value - ema) * multiplier + ema;
      result.push({ time: seriesData[i].time, value: ema });
    }
    return result;
  }, []);

  const calculateMACD = useCallback(
    (data, fast, slow, signal) => {
      if (data.length < slow + signal - 1) return { macd: [], signal: [], hist: [] };
      const fastEma = calculateEMA(data, fast);
      const slowEma = calculateEMA(data, slow);
      const macdLine = data.slice(slow - 1).map((candle, index) => {
        const dataIndex = index + slow - 1;
        return {
          time: candle.time,
          value: (fastEma[dataIndex]?.value ?? 0) - (slowEma[dataIndex]?.value ?? 0),
        };
      });

      const signalLine = calculateEMAOnSeries(macdLine, signal).slice(signal - 1);
      if (!signalLine.length) return { macd: [], signal: [], hist: [] };

      const macdStartTime = signalLine[0].time;
      const trimmedMacdLine = macdLine.filter((point) => point.time >= macdStartTime);
      const signalMap = new Map(signalLine.map((point) => [point.time, point.value]));
      const histogram = trimmedMacdLine.map((point) => ({
        time: point.time,
        value: point.value - (signalMap.get(point.time) ?? 0),
      }));

      return { macd: trimmedMacdLine, signal: signalLine, hist: histogram };
    },
    [calculateEMA, calculateEMAOnSeries],
  );

  const calculateVWAP = useCallback((data) => {
    if (!data.length) return [];
    let cumulativePV = 0;
    let cumulativeVolume = 0;
    return data.map((candle) => {
      const typicalPrice = (candle.high + candle.low + candle.close) / 3;
      const syntheticVolume = Math.max(1, Math.abs(candle.high - candle.low));
      cumulativePV += typicalPrice * syntheticVolume;
      cumulativeVolume += syntheticVolume;
      return {
        time: candle.time,
        value: cumulativePV / cumulativeVolume,
      };
    });
  }, []);

  const calculateATR = useCallback((data, period) => {
    if (data.length < period) return [];
    const trueRanges = [];
    for (let i = 0; i < data.length; i++) {
      const current = data[i];
      const prevClose = i > 0 ? data[i - 1].close : current.close;
      const rangeHighLow = current.high - current.low;
      const rangeHighClose = Math.abs(current.high - prevClose);
      const rangeLowClose = Math.abs(current.low - prevClose);
      trueRanges.push(Math.max(rangeHighLow, rangeHighClose, rangeLowClose));
    }
    const atrValues = [];
    for (let i = period - 1; i < trueRanges.length; i++) {
      const slice = trueRanges.slice(i - period + 1, i + 1);
      const avg = slice.reduce((acc, val) => acc + val, 0) / period;
      atrValues.push({ time: data[i].time, value: avg });
    }
    return atrValues;
  }, []);

  const calculateStochastic = useCallback((data, kPeriod, dPeriod) => {
    if (data.length < kPeriod) return { k: [], d: [] };
    const kValues = [];
    for (let i = kPeriod - 1; i < data.length; i++) {
      const slice = data.slice(i - kPeriod + 1, i + 1);
      const highestHigh = Math.max(...slice.map((c) => c.high));
      const lowestLow = Math.min(...slice.map((c) => c.low));
      const denominator = highestHigh - lowestLow || 1;
      const k = ((data[i].close - lowestLow) / denominator) * 100;
      kValues.push({ time: data[i].time, value: k });
    }
    const dValues = [];
    for (let i = dPeriod - 1; i < kValues.length; i++) {
      const slice = kValues.slice(i - dPeriod + 1, i + 1);
      const avg = slice.reduce((acc, val) => acc + val.value, 0) / dPeriod;
      dValues.push({ time: kValues[i].time, value: avg });
    }
    return { k: kValues, d: dValues };
  }, []);

  const normalizeStdDev = useCallback((rawValue) => {
    const parsed = Number.parseFloat(String(rawValue));
    if (!Number.isFinite(parsed)) return 2;
    return Math.min(Math.max(parsed, 0.5), 5);
  }, []);

  const normalizePeriod = useCallback((rawValue, fallback) => {
    const parsed = Number.parseInt(String(rawValue), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(Math.max(parsed, 2), 500);
  }, []);

  const normalizeMacdSettings = useCallback(
    (settings) => {
      let fast = normalizePeriod(settings.fast, 12);
      let slow = normalizePeriod(settings.slow, 26);
      let signal = normalizePeriod(settings.signal, 9);
      if (slow <= fast) {
        slow = Math.min(fast + 1, 500);
      }
      return {
        fast: String(fast),
        slow: String(slow),
        signal: String(signal),
      };
    },
    [normalizePeriod],
  );

  const normalizeStochSettings = useCallback(
    (settings) => {
      const kPeriod = normalizePeriod(settings.kPeriod, 14);
      const dPeriod = normalizePeriod(settings.dPeriod, 3);
      return { kPeriod: String(kPeriod), dPeriod: String(dPeriod) };
    },
    [normalizePeriod],
  );

  const padIndicatorData = (indicatorData, mainCandles) => {
    if (!indicatorData.length || !mainCandles.length) return indicatorData;
    const firstTime = indicatorData[0].time;
    const padding = [];
    for (let i = 0; i < mainCandles.length; i++) {
      if (mainCandles[i].time >= firstTime) break;
      padding.push({ time: mainCandles[i].time });
    }
    return padding.length > 0 ? [...padding, ...indicatorData] : indicatorData;
  };

  const indicatorMeta = useMemo(
    () => ({
      sma20: { label: "SMA", defaultPeriod: 20, calc: calculateSMA },
      sma50: { label: "SMA", defaultPeriod: 50, calc: calculateSMA },
      ema12: { label: "EMA", defaultPeriod: 12, calc: calculateEMA },
      ema26: { label: "EMA", defaultPeriod: 26, calc: calculateEMA },
      rsi: { label: "RSI", defaultPeriod: 14, calc: calculateRSI },
    }),
    [calculateEMA, calculateRSI, calculateSMA],
  );

  const normalizeTimeKey = useCallback((time) => {
    if (typeof time === "number") return time;
    if (time && typeof time === "object") {
      return Math.floor(new Date(time.year, time.month - 1, time.day).getTime() / 1000);
    }
    return null;
  }, []);

  const formatTimeLabel = useCallback((timeKey) => {
    if (!timeKey) return "—";
    return axisDateTimeFormatter.format(new Date(timeKey * 1000));
  }, []);

  const formatAxisTick = useCallback(
    (time, tickMarkType) => {
      const timeKey = normalizeTimeKey(time);
      if (timeKey == null) return null;

      const date = new Date(timeKey * 1000);

      if (tickMarkType === TickMarkType.TimeWithSeconds) {
        return axisSecondFormatter.format(date);
      }

      if (tickMarkType === TickMarkType.Time) {
        return axisTimeFormatter.format(date);
      }

      return axisDateFormatter.format(date);
    },
    [normalizeTimeKey],
  );

  const getIndicatorValue = useCallback((key, timeKey) => {
    const map = indicatorValueMapRef.current?.[key];
    if (!map || timeKey == null) return null;
    return map.get(timeKey) ?? null;
  }, []);

  const getLastFiniteMapValue = useCallback((map) => {
    if (!(map instanceof Map)) return null;

    let lastValue = null;
    map.forEach((value) => {
      if (Number.isFinite(value)) {
        lastValue = value;
      }
    });

    return lastValue;
  }, []);

  const getHoverInfoAtTime = useCallback((timeKey) => {
    if (timeKey == null) return null;

    const candle = candleMapRef.current.get(timeKey);
    if (!candle) return null;

    return {
      time: timeKey,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    };
  }, []);

  const getChartCrosshairTarget = useCallback(
    (chartKey, timeKey) => {
      if (timeKey == null) return null;

      if (chartKey === "main") {
        const candle = candleMapRef.current.get(timeKey);
        if (!candle || !chartRef.current || !seriesRef.current) return null;
        return { chart: chartRef.current, series: seriesRef.current, price: candle.close };
      }

      if (chartKey === "rsi") {
        if (!rsiChartRef.current || !rsiSeriesRef.current) return null;
        const map = indicatorValueMapRef.current?.rsi;
        const price = getIndicatorValue("rsi", timeKey) ?? getLastFiniteMapValue(map);
        return Number.isFinite(price)
          ? { chart: rsiChartRef.current, series: rsiSeriesRef.current, price }
          : null;
      }

      if (chartKey === "macd") {
        if (!macdChartRef.current) return null;

        const candidates = [
          {
            series: macdSeriesRefs.current.macd,
            price:
              getIndicatorValue("macd", timeKey) ??
              getLastFiniteMapValue(indicatorValueMapRef.current?.macd),
          },
          {
            series: macdSeriesRefs.current.signal,
            price:
              getIndicatorValue("macdSignal", timeKey) ??
              getLastFiniteMapValue(indicatorValueMapRef.current?.macdSignal),
          },
          {
            series: macdSeriesRefs.current.hist,
            price:
              getIndicatorValue("macdHist", timeKey) ??
              getLastFiniteMapValue(indicatorValueMapRef.current?.macdHist),
          },
        ];

        const target = candidates.find((item) => item.series && Number.isFinite(item.price));
        return target
          ? { chart: macdChartRef.current, series: target.series, price: target.price }
          : null;
      }

      if (chartKey === "atr") {
        if (!atrChartRef.current || !atrSeriesRef.current) return null;
        const map = indicatorValueMapRef.current?.atr;
        const price = getIndicatorValue("atr", timeKey) ?? getLastFiniteMapValue(map);
        return Number.isFinite(price)
          ? { chart: atrChartRef.current, series: atrSeriesRef.current, price }
          : null;
      }

      if (chartKey === "stoch") {
        if (!stochChartRef.current) return null;

        const candidates = [
          {
            series: stochSeriesRefs.current.k,
            price:
              getIndicatorValue("stochK", timeKey) ??
              getLastFiniteMapValue(indicatorValueMapRef.current?.stochK),
          },
          {
            series: stochSeriesRefs.current.d,
            price:
              getIndicatorValue("stochD", timeKey) ??
              getLastFiniteMapValue(indicatorValueMapRef.current?.stochD),
          },
        ];

        const target = candidates.find((item) => item.series && Number.isFinite(item.price));
        return target
          ? { chart: stochChartRef.current, series: target.series, price: target.price }
          : null;
      }

      return null;
    },
    [getIndicatorValue, getLastFiniteMapValue],
  );

  const normalizeCandles = useCallback((candleData) => {
    if (!Array.isArray(candleData) || candleData.length === 0) return [];

    return candleData
      .filter((item) => Number.isFinite(item?.time))
      .map((item) => ({
        ...item,
        time: Math.floor(Number(item.time)),
      }))
      .sort((a, b) => a.time - b.time)
      .filter((item, index, self) => index === 0 || item.time > self[index - 1].time);
  }, []);

  const upsertCandle = useCallback(
    (existingCandles, nextCandle) => {
      const candlesList = Array.isArray(existingCandles) ? [...existingCandles] : [];
      const existingIndex = candlesList.findIndex((item) => item.time === nextCandle.time);

      if (existingIndex >= 0) {
        candlesList[existingIndex] = nextCandle;
      } else {
        candlesList.push(nextCandle);
      }

      return normalizeCandles(candlesList);
    },
    [normalizeCandles],
  );

  const fetchOldContractsSymbols = useCallback(async () => {
    if (!accountSetId || !canLoadOldContracts) {
      setOldContractsSymbols([]);
      setOldContractPairs([]);
      setSelectedOldSymbol("");
      setOldContractsSymbolsLoaded(true);
      return;
    }

    setOldContractsSymbolsLoaded(false);

    try {
      const response = await API.get("/old-contracts/symbols", {
        params: { accountSetId },
      });
      if (response.data?.success) {
        const nextSymbols = Array.isArray(response.data.data) ? response.data.data : [];
        const nextPairs = Array.isArray(response.data.pairs) ? response.data.pairs : [];
        setOldContractsSymbols(nextSymbols);
        setOldContractPairs(nextPairs);
        setSelectedOldSymbol((currentValue) =>
          currentValue && nextSymbols.includes(currentValue) ? currentValue : nextSymbols[0] || "",
        );
      } else {
        setOldContractsSymbols([]);
        setOldContractPairs([]);
        setSelectedOldSymbol("");
      }
    } catch (err) {
      setOldContractsSymbols([]);
      setOldContractPairs([]);
      setSelectedOldSymbol("");
      console.error("Error fetching old contracts symbols:", err);
    } finally {
      setOldContractsSymbolsLoaded(true);
    }
  }, [accountSetId, canLoadOldContracts]);

  const resolveViewportActionForFetchResult = useCallback(
    (fetchContext, viewportMode, totalBars) =>
      getChartViewportAction({
        viewportMode,
        previousViewKey: fetchContext.previousViewKey,
        nextViewKey: currentChartViewKey,
        initialLoaded: fetchContext.initialLoaded,
        shouldResetViewport: fetchContext.shouldResetViewport,
        sourceCandleCount: fetchContext.sourceCandleCount,
        minSourceCandles: CHART_VIEWPORT_RESTORE_MIN_SOURCE_CANDLES,
        logicalRange: fetchContext.logicalRange,
        totalBars,
      }),
    [currentChartViewKey],
  );

  const fetchCandles = useCallback(
    async ({ viewportMode = VIEWPORT_MODE_RESET } = {}) => {
      const requestId = ++fetchRequestIdRef.current;
      const shouldShowBlockingLoader = candlesRef.current.length === 0;
      const fetchContext = {
        previousViewKey: lastLoadedChartViewKeyRef.current,
        initialLoaded: initialLoaded.current,
        shouldResetViewport: resetViewportOnNextLoadRef.current,
        sourceCandleCount: candlesRef.current.length,
        logicalRange:
          visibleLogicalRangeRef.current ??
          safelyRunChartOperation(() => chartRef.current?.timeScale().getVisibleLogicalRange()) ??
          null,
      };

      if (isOldContractsMode) {
        if (!canLoadOldContracts) {
          candlesRef.current = [];
          setCandles([]);
          setLatestCandle(null);
          lastCandleTimeRef.current = null;
          pendingViewportActionRef.current = null;
          lastLoadedChartViewKeyRef.current = null;
          setConnectionStatus("no-data");
          setLoading(false);
          setFetching(false);
          return;
        }

        if (!selectedOldSymbol) {
          debugChart("No old contract symbol selected");
          candlesRef.current = [];
          setCandles([]);
          setLatestCandle(null);
          lastCandleTimeRef.current = null;
          pendingViewportActionRef.current = null;
          lastLoadedChartViewKeyRef.current = null;
          setConnectionStatus("no-data");
          return;
        }

        debugChart(
          `Fetching old contract ${premiumChartSide} candles: ${selectedOldSymbol} ${oldContractTimeframe}`,
        );
        if (shouldShowBlockingLoader) setLoading(true);
        else setFetching(true);
        setError("");

        try {
          const pageSize =
            maxPoints !== null
              ? Math.max(
                  1,
                  Math.min(
                    Number(maxPoints) || OLD_CONTRACTS_MAX_PAGE_SIZE,
                    OLD_CONTRACTS_MAX_PAGE_SIZE,
                  ),
                )
              : OLD_CONTRACTS_MAX_PAGE_SIZE;
          oldContractsPageSizeRef.current = pageSize;

          const params = {
            accountSetId,
            symbol: selectedOldSymbol,
            timeframe: oldContractTimeframe,
            page: 1,
            pageSize,
            side: premiumChartSide,
          };

          if (daysToFetch !== null) params.days = daysToFetch;

          const response = await API.get("/old-contracts/candles", { params });

          if (requestId !== fetchRequestIdRef.current) return;
          if (response.data?.success !== true) {
            throw new Error(
              response.data?.error || response.data?.message || "Failed to load old contract data",
            );
          }

          const rawCandles = Array.isArray(response.data?.data) ? response.data.data : [];
          const pagination = response.data?.pagination;
          oldContractsNextPageRef.current = pagination?.hasNextPage ? 2 : null;
          hasMoreDataRef.current = maxPoints === null ? Boolean(pagination?.hasNextPage) : false;

          if (rawCandles.length > 0) {
            const candleData = normalizeCandles(
              rawCandles.map((bar) => ({
                time: Math.floor(Number(bar.time)),
                open: Number(bar.open) || Number(bar.close) || 0,
                high: Number(bar.high) || Number(bar.close) || 0,
                low: Number(bar.low) || Number(bar.close) || 0,
                close: Number(bar.close) || 0,
              })),
            );

            debugChart(`Loaded ${candleData.length} old contract ${premiumChartSide} candles`);
            const viewportAction = resolveViewportActionForFetchResult(
              fetchContext,
              viewportMode,
              candleData.length,
            );
            candlesRef.current = candleData;
            setCandles(candleData);
            oldestCandleTimeRef.current = candleData.length > 0 ? candleData[0].time : null;
            const lastCandle = candleData[candleData.length - 1] || null;
            setLatestCandle(lastCandle);
            lastCandleTimeRef.current = lastCandle?.time || null;
            pendingViewportActionRef.current = viewportAction;
            lastLoadedChartViewKeyRef.current = currentChartViewKey;

            setConnectionStatus(candleData.length ? "connected" : "no-data");
            if (!replayActiveRef.current && (candleSeriesRef.current || areaSeriesRef.current)) {
              candleSeriesRef.current?.setData(candleData);
              areaSeriesRef.current?.setData(toAreaSeriesData(candleData));
              initialLoaded.current = true;
              applyViewportAction(viewportAction, candleData.length);
            }
          } else {
            debugChartWarn("No old contract candle data received");
            candlesRef.current = [];
            setCandles([]);
            setLatestCandle(null);
            lastCandleTimeRef.current = null;
            oldestCandleTimeRef.current = null;
            oldContractsNextPageRef.current = null;
            hasMoreDataRef.current = false;
            pendingViewportActionRef.current = null;
            lastLoadedChartViewKeyRef.current = null;
            setConnectionStatus("no-data");
          }
        } catch (err) {
          if (requestId !== fetchRequestIdRef.current) return;
          console.error("Error fetching old contract candles:", err);
          setError("Failed to load old contract data");
          setConnectionStatus("error");
        } finally {
          if (requestId === fetchRequestIdRef.current) {
            setLoading(false);
            setFetching(false);
          }
        }
      } else {
        if (!accountSetId) {
          debugChart("Missing accountSetId");
          pendingViewportActionRef.current = null;
          lastLoadedChartViewKeyRef.current = null;
          return;
        }

        debugChart(
          `Fetching ${premiumChartSide} candles: ${tf}m timeframe for accountSet ${accountSetId}`,
        );
        if (shouldShowBlockingLoader) setLoading(true);
        else setFetching(true);
        setError("");

        try {
          const params = {
            tf: tf,
            accountSetId: accountSetId,
            limit: maxPoints !== null ? maxPoints : LIVE_PREMIUM_PAGE_SIZE,
            side: premiumChartSide,
          };

          if (daysToFetch !== null) params.days = daysToFetch;

          const response = await API.get("/premium-candles", { params });

          if (requestId !== fetchRequestIdRef.current) return;

          if (response.data?.success && response.data?.data) {
            const candleData = normalizeCandles(
              response.data.data.map((bar) => ({
                time: Math.floor(Number(bar.time)),
                open: Number(bar.open) || Number(bar.close) || 0,
                high: Number(bar.high) || Number(bar.close) || 0,
                low: Number(bar.low) || Number(bar.close) || 0,
                close: Number(bar.close) || 0,
              })),
            );

            debugChart(
              `Loaded ${candleData.length} ${premiumChartSide} candles (${tf}m timeframe)`,
            );
            const viewportAction = resolveViewportActionForFetchResult(
              fetchContext,
              viewportMode,
              candleData.length,
            );
            candlesRef.current = candleData;
            setCandles(candleData);
            oldestCandleTimeRef.current = candleData.length > 0 ? candleData[0].time : null;
            hasMoreDataRef.current = response.data.hasMore !== false;
            const lastCandle = candleData[candleData.length - 1] || null;
            setLatestCandle(lastCandle);
            lastCandleTimeRef.current = lastCandle?.time || null;
            pendingViewportActionRef.current = viewportAction;
            lastLoadedChartViewKeyRef.current = currentChartViewKey;

            setConnectionStatus(candleData.length ? "connected" : "no-data");
            if (!replayActiveRef.current && (candleSeriesRef.current || areaSeriesRef.current)) {
              candleSeriesRef.current?.setData(candleData);
              areaSeriesRef.current?.setData(toAreaSeriesData(candleData));
              initialLoaded.current = true;
              applyViewportAction(viewportAction, candleData.length);
            }
          } else {
            debugChartWarn("No candle data received");
            candlesRef.current = [];
            setCandles([]);
            setLatestCandle(null);
            lastCandleTimeRef.current = null;
            pendingViewportActionRef.current = null;
            lastLoadedChartViewKeyRef.current = null;
            setConnectionStatus("no-data");
          }
        } catch (err) {
          if (requestId !== fetchRequestIdRef.current) return;
          console.error("Error fetching candles:", err);
          setError("Failed to load chart data");
          setConnectionStatus("error");
        } finally {
          if (requestId === fetchRequestIdRef.current) {
            setLoading(false);
            setFetching(false);
          }
        }
      }
    },
    [
      accountSetId,
      applyViewportAction,
      canLoadOldContracts,
      currentChartViewKey,
      daysToFetch,
      isOldContractsMode,
      maxPoints,
      normalizeCandles,
      oldContractTimeframe,
      premiumChartSide,
      resolveViewportActionForFetchResult,
      safelyRunChartOperation,
      selectedOldSymbol,
      tf,
      toAreaSeriesData,
    ],
  );

  const fetchOlderCandles = useCallback(async () => {
    if (isFetchingOlderRef.current || !hasMoreDataRef.current) return;
    if (!accountSetId) return;

    const activeRequestId = fetchRequestIdRef.current;
    isFetchingOlderRef.current = true;

    try {
      let olderCandles = [];
      let hasMore = false;

      if (isOldContractsMode) {
        const nextPage = oldContractsNextPageRef.current;
        if (!selectedOldSymbol || !Number.isFinite(Number(nextPage))) {
          hasMoreDataRef.current = false;
          oldContractsNextPageRef.current = null;
          return;
        }

        const params = {
          accountSetId,
          symbol: selectedOldSymbol,
          timeframe: oldContractTimeframe,
          page: Number(nextPage),
          pageSize: oldContractsPageSizeRef.current || OLD_CONTRACTS_MAX_PAGE_SIZE,
          side: premiumChartSide,
        };

        if (daysToFetch !== null) params.days = daysToFetch;

        const response = await API.get("/old-contracts/candles", { params });

        if (activeRequestId !== fetchRequestIdRef.current) return;
        if (!response.data?.success || !response.data?.data?.length) {
          hasMoreDataRef.current = false;
          oldContractsNextPageRef.current = null;
          return;
        }

        olderCandles = normalizeCandles(
          response.data.data.map((bar) => ({
            time: Math.floor(Number(bar.time)),
            open: Number(bar.open) || Number(bar.close) || 0,
            high: Number(bar.high) || Number(bar.close) || 0,
            low: Number(bar.low) || Number(bar.close) || 0,
            close: Number(bar.close) || 0,
          })),
        );

        hasMore = Boolean(response.data?.pagination?.hasNextPage);
        oldContractsNextPageRef.current = hasMore ? Number(nextPage) + 1 : null;
      } else {
        if (!oldestCandleTimeRef.current) return;

        const params = {
          tf,
          accountSetId,
          before: oldestCandleTimeRef.current,
          limit: LIVE_PREMIUM_PAGE_SIZE,
          side: premiumChartSide,
        };

        if (daysToFetch !== null) params.days = daysToFetch;

        const response = await API.get("/premium-candles", { params });

        if (activeRequestId !== fetchRequestIdRef.current) return;
        if (!response.data?.success || !response.data?.data?.length) {
          hasMoreDataRef.current = false;
          return;
        }

        olderCandles = normalizeCandles(
          response.data.data.map((bar) => ({
            time: Math.floor(Number(bar.time)),
            open: Number(bar.open) || Number(bar.close) || 0,
            high: Number(bar.high) || Number(bar.close) || 0,
            low: Number(bar.low) || Number(bar.close) || 0,
            close: Number(bar.close) || 0,
          })),
        );

        hasMore = response.data.hasMore !== false;
      }

      if (activeRequestId !== fetchRequestIdRef.current) return;
      if (olderCandles.length === 0) {
        hasMoreDataRef.current = false;
        return;
      }

      hasMoreDataRef.current = hasMore;

      const existingTimes = new Set(candlesRef.current.map((c) => c.time));
      const newCandles = olderCandles.filter((c) => !existingTimes.has(c.time));

      if (newCandles.length === 0) {
        hasMoreDataRef.current = false;
        return;
      }

      const merged = normalizeCandles([...newCandles, ...candlesRef.current]);
      const timeScale = chartRef.current?.timeScale();
      const currentLogicalRange = timeScale?.getVisibleLogicalRange();

      candlesRef.current = merged;
      setCandles(merged);
      oldestCandleTimeRef.current = merged[0]?.time ?? null;

      if (!replayActiveRef.current) {
        candleSeriesRef.current?.setData(merged);
        areaSeriesRef.current?.setData(toAreaSeriesData(merged));
      }

      if (currentLogicalRange && timeScale) {
        const shift = newCandles.length;
        runProgrammaticTimeScaleUpdate(() => {
          timeScale.setVisibleLogicalRange({
            from: currentLogicalRange.from + shift,
            to: currentLogicalRange.to + shift,
          });
        });
      }
    } catch (err) {
      console.error("Error fetching older candles:", err);
    } finally {
      isFetchingOlderRef.current = false;
    }
  }, [
    accountSetId,
    tf,
    daysToFetch,
    isOldContractsMode,
    normalizeCandles,
    oldContractTimeframe,
    premiumChartSide,
    runProgrammaticTimeScaleUpdate,
    selectedOldSymbol,
    toAreaSeriesData,
  ]);

  const handleRealtimeUpdate = useCallback(
    (data) => {
      if (!chartRef.current || (!candleSeriesRef.current && !areaSeriesRef.current)) return;

      const newPrice = getRealtimePremiumValue(data, premiumChartSide);
      if (newPrice === null) {
        debugChart("Invalid premium price data received:", data);
        debugChart("Available data fields:", Object.keys(data));
        return;
      }

      const nowSec = Math.floor(Date.now() / 1000);
      const intervalSec = tf * 60;
      const candleTimeSec = Math.floor(nowSec / intervalSec) * intervalSec;

      setCurrentPrice(newPrice);

      setCurrentCandleHighlight(true);
      setLastUpdate(Date.now());
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => setCurrentCandleHighlight(false), 900);

      const currentCandles = candlesRef.current;
      const lastCandle = currentCandles[currentCandles.length - 1];

      let nextCandle;

      if (!lastCandle || lastCandle.time !== candleTimeSec) {
        if (lastCandleTimeRef.current && lastCandleTimeRef.current !== candleTimeSec) {
          debugChart("New candle period detected, continuing with real-time updates");
        }

        lastCandleTimeRef.current = candleTimeSec;
        nextCandle = {
          time: Math.floor(candleTimeSec),
          open: newPrice,
          high: newPrice,
          low: newPrice,
          close: newPrice,
          isRealTime: true,
        };
      } else {
        nextCandle = {
          time: Math.floor(candleTimeSec),
          open: lastCandle.open,
          high: Math.max(lastCandle.high, newPrice),
          low: Math.min(lastCandle.low, newPrice),
          close: newPrice,
          isRealTime: true,
        };
      }

      const updatedCandles = upsertCandle(currentCandles, nextCandle);
      const changedCandleIsLatest =
        updatedCandles[updatedCandles.length - 1]?.time === nextCandle.time;

      candlesRef.current = updatedCandles;
      setCandles(updatedCandles);
      setLatestCandle(updatedCandles[updatedCandles.length - 1] || nextCandle);

      if (!replayActiveRef.current) {
        if (changedCandleIsLatest) {
          candleSeriesRef.current?.update(nextCandle);
          areaSeriesRef.current?.update({
            time: nextCandle.time,
            value: nextCandle.close,
          });
        } else {
          candleSeriesRef.current?.setData(updatedCandles);
          areaSeriesRef.current?.setData(toAreaSeriesData(updatedCandles));
        }
      }

      const timeScale = chartRef.current.timeScale();
      const visibleRange = timeScale.getVisibleRange();
      if (autoFollowRef.current && visibleRange && visibleRange.to > candleTimeSec - tf * 60 * 10) {
        timeScale.scrollToRealTime();
      }

      setConnectionStatus("live");
    },
    [premiumChartSide, tf, toAreaSeriesData, upsertCandle],
  );

  useEffect(() => {
    if (!chartContainer.current) return;

    debugChart("Initializing enhanced chart...");

    const chart = createChart(chartContainer.current, {
      width: chartContainer.current.clientWidth,
      height: 400,
      layout: {
        background: { color: CHART_COLORS.card },
        textColor: CHART_COLORS.fgDim,
        attributionLogo: false,
      },
      grid: buildGridOptions({ showVertical: false, showHorizontal: true }),
      timeScale: {
        timeVisible: true,
        borderColor: CHART_COLORS.borderStrong,
        fixLeftEdge: true,
        fixRightEdge: false,
        barSpacing: 12,
        minBarSpacing: 10,
        tickMarkFormatter: formatAxisTick,
      },
      localization: {
        locale: "en-GB",
        priceFormatter: formatPremiumValue,
        timeFormatter: (time) => {
          const timeKey = normalizeTimeKey(time);
          return timeKey == null ? "" : axisSecondFormatter.format(new Date(timeKey * 1000));
        },
      },
      rightPriceScale: {
        borderColor: CHART_COLORS.borderStrong,
        minimumWidth: 0,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      leftPriceScale: {
        visible: false,
        borderColor: CHART_COLORS.borderStrong,
      },
      crosshair: {
        mode: 1,
        ...buildCrosshairOptions(CHART_COLORS.muted, { width: 1, style: 3 }),
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
    });

    const candlestickSeries = addCandlestickSeries(chart, {
      upColor: CHART_COLORS.emerald,
      downColor: CHART_COLORS.rose,
      borderVisible: false,
      wickUpColor: CHART_COLORS.emerald,
      wickDownColor: CHART_COLORS.rose,
      borderUpColor: CHART_COLORS.emerald,
      borderDownColor: CHART_COLORS.rose,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
      priceScaleId: "right",
    });
    const areaSeries = addAreaSeries(chart, {
      ...buildMainAreaSeriesOptions(),
      visible: false,
      priceScaleId: "right",
    });

    chartRef.current = chart;
    candleSeriesRef.current = candlestickSeries;
    areaSeriesRef.current = areaSeries;
    seriesRef.current = candlestickSeries;
    indicatorSeriesRefs.current = {};

    try {
      const mainPane = chart.panes?.()[0];
      if (mainPane) {
        textWatermarkRef.current = createTextWatermark(mainPane, buildMainTextWatermarkOptions());
      }
    } catch {}

    const cleanupResizeObserver = attachChartResizeObserver(chartContainer.current, chart);

    debugChart("Enhanced chart initialized");

    return () => {
      clearOrderPriceLines();
      chartRef.current = null;
      seriesRef.current = null;
      candleSeriesRef.current = null;
      areaSeriesRef.current = null;
      textWatermarkRef.current?.detach?.();
      textWatermarkRef.current = null;
      indicatorSeriesRefs.current = {};
      cleanupResizeObserver();
      safelyRunChartOperation(() => {
        chart.remove();
      });
    };
  }, [
    attachChartResizeObserver,
    clearOrderPriceLines,
    formatAxisTick,
    formatPremiumValue,
    normalizeTimeKey,
    safelyRunChartOperation,
  ]);

  useEffect(() => {
    const showArea = chartStyle === "area";

    candleSeriesRef.current?.applyOptions({
      visible: !showArea,
    });
    areaSeriesRef.current?.applyOptions({
      visible: showArea,
    });
    seriesRef.current = showArea ? areaSeriesRef.current : candleSeriesRef.current;
  }, [chartStyle]);

  useEffect(() => {
    clearOrderPriceLines();

    if (!ordersOverlayEnabled || isOldContractsMode || stableOrderOverlays.length === 0) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const anchorSeries = chartStyle === "area" ? areaSeriesRef.current : candleSeriesRef.current;
      if (!anchorSeries) return;

      stableOrderOverlays.forEach((group) => {
        const showLabel = isMobileViewport
          ? mobileOrderOverlayLabelKeys.includes(buildOrderOverlayGroupKey(group.price))
          : true;
        const priceLine = anchorSeries.createPriceLine(
          buildOrderPriceLineOptions(group, isMobileViewport, showLabel),
        );
        orderPriceLinesRef.current.push({ line: priceLine, series: anchorSeries });
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      clearOrderPriceLines();
    };
  }, [
    chartStyle,
    clearOrderPriceLines,
    isMobileViewport,
    isOldContractsMode,
    mobileOrderOverlayLabelKeys,
    stableOrderOverlays,
    ordersOverlayEnabled,
    themeTick,
  ]);

  useEffect(() => {
    textWatermarkRef.current?.applyOptions(
      buildMainTextWatermarkOptions({ isMobile: isMobileViewport }),
    );
  }, [isMobileViewport, themeTick]);

  useEffect(() => {
    const container = chartContainer.current;
    if (!container) return;

    const armHistoryLoad = () => {
      historyLoadArmedRef.current = true;
    };

    container.addEventListener("wheel", armHistoryLoad, { passive: true });
    container.addEventListener("mousedown", armHistoryLoad);
    container.addEventListener("touchstart", armHistoryLoad, { passive: true });

    return () => {
      container.removeEventListener("wheel", armHistoryLoad);
      container.removeEventListener("mousedown", armHistoryLoad);
      container.removeEventListener("touchstart", armHistoryLoad);
    };
  }, []);

  useEffect(() => {
    if (!indicatorSettings.rsi.enabled) return;
    if (!rsiContainerRef.current || rsiChartRef.current) return;

    const rsiChart = createChart(rsiContainerRef.current, {
      width: rsiContainerRef.current.clientWidth,
      height: 140,
      layout: {
        background: { color: CHART_COLORS.card },
        textColor: CHART_COLORS.mutedLight,
        attributionLogo: false,
      },
      grid: buildGridOptions({ showVertical: false, showHorizontal: true }),
      timeScale: {
        timeVisible: false,
        borderVisible: false,
        visible: false,
        fixLeftEdge: false,
        fixRightEdge: false,
        barSpacing: 12,
        minBarSpacing: 10,
      },
      rightPriceScale: {
        borderColor: CHART_COLORS.borderStrong,
        minimumWidth: 0,
        scaleMargins: { top: 0.15, bottom: 0.15 },
      },
      crosshair: {
        ...buildCrosshairOptions(CHART_COLORS.muted, { width: 1, style: 2 }),
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
    });

    const rsiSeries = addLineSeries(rsiChart, {
      color: CHART_COLORS.blue,
      lineWidth: 2,
      title: "RSI",
      priceLineVisible: false,
      lastValueVisible: true,
    });

    rsiChartRef.current = rsiChart;
    rsiSeriesRef.current = rsiSeries;
    setRsiReady(true);

    rsiPriceLinesRef.current.upper = rsiSeries.createPriceLine({
      price: 70,
      color: CHART_COLORS.rose,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: "70",
    });
    rsiPriceLinesRef.current.lower = rsiSeries.createPriceLine({
      price: 30,
      color: CHART_COLORS.emerald,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: "30",
    });

    const cleanupResizeObserver = attachChartResizeObserver(rsiContainerRef.current, rsiChart);

    return () => {
      rsiChartRef.current = null;
      rsiSeriesRef.current = null;
      rsiPriceLinesRef.current = { upper: null, lower: null };
      cleanupResizeObserver();
      safelyRunChartOperation(() => {
        rsiChart.remove();
      });
      setRsiReady(false);
    };
  }, [attachChartResizeObserver, indicatorSettings.rsi.enabled, safelyRunChartOperation]);

  useEffect(() => {
    if (!indicatorSettings.macd.enabled) return;
    if (!macdContainerRef.current || macdChartRef.current) return;

    const macdChart = createChart(macdContainerRef.current, {
      width: macdContainerRef.current.clientWidth,
      height: 160,
      layout: {
        background: { color: CHART_COLORS.card },
        textColor: CHART_COLORS.mutedLight,
        attributionLogo: false,
      },
      grid: buildGridOptions({ showVertical: false, showHorizontal: true }),
      timeScale: {
        timeVisible: false,
        borderVisible: false,
        visible: false,
        fixLeftEdge: false,
        fixRightEdge: false,
        barSpacing: 12,
        minBarSpacing: 10,
      },
      rightPriceScale: {
        borderColor: CHART_COLORS.borderStrong,
        minimumWidth: 0,
        scaleMargins: { top: 0.2, bottom: 0.2 },
      },
      crosshair: {
        ...buildCrosshairOptions(CHART_COLORS.muted, { width: 1, style: 2 }),
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
    });

    const macdHistogram = addHistogramSeries(macdChart, {
      priceScaleId: "right",
      priceFormat: { type: "price", precision: 4, minMove: 0.0001 },
      base: 0,
    });
    const macdLine = addLineSeries(macdChart, {
      color: CHART_COLORS.rose,
      lineWidth: 2,
      title: "MACD",
      priceLineVisible: false,
      lastValueVisible: true,
    });
    const signalLine = addLineSeries(macdChart, {
      color: CHART_COLORS.blueLight,
      lineWidth: 1,
      title: "Signal",
      priceLineVisible: false,
      lastValueVisible: true,
    });

    macdSeriesRefs.current = { macd: macdLine, signal: signalLine, hist: macdHistogram };
    macdChartRef.current = macdChart;
    setMacdReady(true);

    macdHistogram.createPriceLine({
      price: 0,
      color: CHART_COLORS.muted,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: "0",
    });

    const cleanupResizeObserver = attachChartResizeObserver(macdContainerRef.current, macdChart);

    return () => {
      macdChartRef.current = null;
      macdSeriesRefs.current = { macd: null, signal: null, hist: null };
      cleanupResizeObserver();
      safelyRunChartOperation(() => {
        macdChart.remove();
      });
      setMacdReady(false);
    };
  }, [attachChartResizeObserver, indicatorSettings.macd.enabled, safelyRunChartOperation]);

  useEffect(() => {
    if (!indicatorSettings.atr.enabled) return;
    if (!atrContainerRef.current || atrChartRef.current) return;

    const atrChart = createChart(atrContainerRef.current, {
      width: atrContainerRef.current.clientWidth,
      height: 140,
      layout: {
        background: { color: CHART_COLORS.card },
        textColor: CHART_COLORS.mutedLight,
        attributionLogo: false,
      },
      grid: buildGridOptions({ showVertical: false, showHorizontal: true }),
      timeScale: {
        timeVisible: false,
        borderVisible: false,
        visible: false,
        fixLeftEdge: false,
        fixRightEdge: false,
        barSpacing: 12,
        minBarSpacing: 10,
      },
      rightPriceScale: {
        borderColor: CHART_COLORS.borderStrong,
        minimumWidth: 0,
        scaleMargins: { top: 0.2, bottom: 0.2 },
      },
      crosshair: {
        ...buildCrosshairOptions(CHART_COLORS.muted, { width: 1, style: 2 }),
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
    });

    const atrSeries = addLineSeries(atrChart, {
      color: CHART_COLORS.lime,
      lineWidth: 2,
      title: "ATR",
      priceLineVisible: false,
      lastValueVisible: true,
    });

    atrChartRef.current = atrChart;
    atrSeriesRef.current = atrSeries;
    setAtrReady(true);

    const cleanupResizeObserver = attachChartResizeObserver(atrContainerRef.current, atrChart);

    return () => {
      atrChartRef.current = null;
      atrSeriesRef.current = null;
      cleanupResizeObserver();
      safelyRunChartOperation(() => {
        atrChart.remove();
      });
      setAtrReady(false);
    };
  }, [attachChartResizeObserver, indicatorSettings.atr.enabled, safelyRunChartOperation]);

  useEffect(() => {
    if (!indicatorSettings.stoch.enabled) return;
    if (!stochContainerRef.current || stochChartRef.current) return;

    const stochChart = createChart(stochContainerRef.current, {
      width: stochContainerRef.current.clientWidth,
      height: 150,
      layout: {
        background: { color: CHART_COLORS.card },
        textColor: CHART_COLORS.mutedLight,
        attributionLogo: false,
      },
      grid: buildGridOptions({ showVertical: false, showHorizontal: true }),
      timeScale: {
        timeVisible: false,
        borderVisible: false,
        visible: false,
        fixLeftEdge: false,
        fixRightEdge: false,
        barSpacing: 12,
        minBarSpacing: 10,
      },
      rightPriceScale: {
        borderColor: CHART_COLORS.borderStrong,
        minimumWidth: 0,
        scaleMargins: { top: 0.2, bottom: 0.2 },
      },
      crosshair: {
        ...buildCrosshairOptions(CHART_COLORS.muted, { width: 1, style: 2 }),
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: true,
        },
        axisDoubleClickReset: {
          time: true,
          price: true,
        },
        mouseWheel: true,
        pinch: true,
      },
    });

    const kSeries = addLineSeries(stochChart, {
      color: CHART_COLORS.cyan,
      lineWidth: 2,
      title: "Stoch %K",
      priceLineVisible: false,
      lastValueVisible: true,
    });
    const dSeries = addLineSeries(stochChart, {
      color: CHART_COLORS.amberLight,
      lineWidth: 1,
      title: "Stoch %D",
      priceLineVisible: false,
      lastValueVisible: true,
    });

    stochSeriesRefs.current = { k: kSeries, d: dSeries };
    stochChartRef.current = stochChart;

    stochPriceLinesRef.current.upper = kSeries.createPriceLine({
      price: 80,
      color: CHART_COLORS.rose,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: "80",
    });
    stochPriceLinesRef.current.lower = kSeries.createPriceLine({
      price: 20,
      color: CHART_COLORS.emerald,
      lineWidth: 1,
      lineStyle: 2,
      axisLabelVisible: true,
      title: "20",
    });

    setStochReady(true);

    const cleanupResizeObserver = attachChartResizeObserver(stochContainerRef.current, stochChart);

    return () => {
      stochChartRef.current = null;
      stochSeriesRefs.current = { k: null, d: null };
      stochPriceLinesRef.current = { upper: null, lower: null };
      cleanupResizeObserver();
      safelyRunChartOperation(() => {
        stochChart.remove();
      });
      setStochReady(false);
    };
  }, [attachChartResizeObserver, indicatorSettings.stoch.enabled, safelyRunChartOperation]);

  useEffect(() => {
    if (!chartRef.current) return;

    const syncLogicalRange = (range) => {
      visibleLogicalRangeRef.current = range;
      if (!programmaticRangeChangeRef.current) {
        autoFollowRef.current = false;
      }
      if (
        range &&
        historyLoadArmedRef.current &&
        !replayActive &&
        !autoFollowRef.current &&
        range.from < 10 &&
        !isFetchingOlderRef.current &&
        hasMoreDataRef.current
      ) {
        fetchOlderCandles();
      }
      if (range) {
        runProgrammaticTimeScaleUpdate(() => {
          safelyRunChartOperation(() =>
            rsiChartRef.current?.timeScale().setVisibleLogicalRange(range),
          );
          safelyRunChartOperation(() =>
            macdChartRef.current?.timeScale().setVisibleLogicalRange(range),
          );
          safelyRunChartOperation(() =>
            atrChartRef.current?.timeScale().setVisibleLogicalRange(range),
          );
          safelyRunChartOperation(() =>
            stochChartRef.current?.timeScale().setVisibleLogicalRange(range),
          );
        });
      }
    };

    const timeScale = chartRef.current.timeScale();
    safelyRunChartOperation(() => timeScale.subscribeVisibleLogicalRangeChange(syncLogicalRange));
    syncLogicalRange(safelyRunChartOperation(() => timeScale.getVisibleLogicalRange()));

    return () => {
      safelyRunChartOperation(() =>
        chartRef.current?.timeScale().unsubscribeVisibleLogicalRangeChange(syncLogicalRange),
      );
    };
  }, [fetchOlderCandles, replayActive, runProgrammaticTimeScaleUpdate, safelyRunChartOperation]);

  useEffect(() => {
    const mainChart = chartRef.current;
    if (!mainChart) return;

    const bindChildRangeSync = (childChart) => {
      if (!childChart) return () => {};

      const handleChildLogicalRangeChange = (range) => {
        if (!range || programmaticRangeChangeRef.current) return;
        autoFollowRef.current = false;
        runProgrammaticTimeScaleUpdate(() => {
          safelyRunChartOperation(() => mainChart.timeScale().setVisibleLogicalRange(range));
        });
      };

      safelyRunChartOperation(() =>
        childChart.timeScale().subscribeVisibleLogicalRangeChange(handleChildLogicalRangeChange),
      );
      return () => {
        safelyRunChartOperation(() =>
          childChart
            .timeScale()
            .unsubscribeVisibleLogicalRangeChange(handleChildLogicalRangeChange),
        );
      };
    };

    const cleanups = [
      bindChildRangeSync(rsiChartRef.current),
      bindChildRangeSync(macdChartRef.current),
      bindChildRangeSync(atrChartRef.current),
      bindChildRangeSync(stochChartRef.current),
    ];

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [
    atrReady,
    indicatorSettings.atr.enabled,
    indicatorSettings.macd.enabled,
    indicatorSettings.rsi.enabled,
    indicatorSettings.stoch.enabled,
    macdReady,
    rsiReady,
    safelyRunChartOperation,
    stochReady,
    runProgrammaticTimeScaleUpdate,
  ]);

  useEffect(() => {
    const chartBindings = [
      { key: "main", chart: chartRef.current },
      { key: "rsi", chart: rsiChartRef.current },
      { key: "macd", chart: macdChartRef.current },
      { key: "atr", chart: atrChartRef.current },
      { key: "stoch", chart: stochChartRef.current },
    ].filter((item) => item.chart);

    if (!chartBindings.length) return;

    const syncOtherCharts = (sourceKey, timeKey) => {
      programmaticCrosshairSyncRef.current = true;
      try {
        chartBindings.forEach(({ key, chart }) => {
          if (!chart || key === sourceKey) return;

          const target = getChartCrosshairTarget(key, timeKey);
          if (!target) {
            safelyRunChartOperation(() => chart.clearCrosshairPosition());
            return;
          }

          safelyRunChartOperation(() =>
            chart.setCrosshairPosition(target.price, timeKey, target.series),
          );
        });
      } finally {
        programmaticCrosshairSyncRef.current = false;
      }
    };

    const clearOtherCharts = (sourceKey) => {
      programmaticCrosshairSyncRef.current = true;
      try {
        chartBindings.forEach(({ key, chart }) => {
          if (!chart || key === sourceKey) return;
          safelyRunChartOperation(() => chart.clearCrosshairPosition());
        });
      } finally {
        programmaticCrosshairSyncRef.current = false;
      }
    };

    const cleanups = chartBindings.map(({ key, chart }) => {
      const handleCrosshairMove = (param) => {
        if (programmaticCrosshairSyncRef.current) return;

        const timeKey = normalizeTimeKey(param?.time);
        if (timeKey == null) {
          setHoverInfo(null);
          clearOtherCharts(key);
          return;
        }

        setHoverInfo(getHoverInfoAtTime(timeKey));
        syncOtherCharts(key, timeKey);
      };

      safelyRunChartOperation(() => chart.subscribeCrosshairMove(handleCrosshairMove));
      return () => {
        safelyRunChartOperation(() => chart.unsubscribeCrosshairMove(handleCrosshairMove));
      };
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [
    atrReady,
    getChartCrosshairTarget,
    getHoverInfoAtTime,
    indicatorSettings.atr.enabled,
    indicatorSettings.macd.enabled,
    indicatorSettings.rsi.enabled,
    indicatorSettings.stoch.enabled,
    macdReady,
    normalizeTimeKey,
    rsiReady,
    safelyRunChartOperation,
    stochReady,
  ]);

  useEffect(() => {
    if (!indicatorPanelOpen) return;
    const handleClickOutside = (event) => {
      if (!indicatorPanelRef.current) return;
      if (!indicatorPanelRef.current.contains(event.target)) {
        setIndicatorPanelOpen(false);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [indicatorPanelOpen]);

  useEffect(() => {
    if (!chartRef.current) return;
    const fallbackHeight = 400;
    const containerHeight = chartContainer.current?.clientHeight || fallbackHeight;
    safelyRunChartOperation(() => {
      chartRef.current?.applyOptions({
        height: containerHeight,
        rightPriceScale: {
          scaleMargins: {
            top: 0.1,
            bottom: 0.1,
          },
        },
      });
    });
  }, [isFullscreen, safelyRunChartOperation]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const shell =
      fullscreenRootRef.current?.closest?.(".app-shell-private") ||
      document.querySelector(".app-shell-private");
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousShellOverflow = shell?.style.overflow;
    const previousShellOverflowX = shell?.style.overflowX;
    const previousShellOverflowY = shell?.style.overflowY;
    const previousShellOverscroll = shell?.style.overscrollBehavior;

    if (isFullscreen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      if (shell) {
        shell.style.overflow = "hidden";
        shell.style.overflowX = "hidden";
        shell.style.overflowY = "hidden";
        shell.style.overscrollBehavior = "contain";
      }
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      if (shell) {
        shell.style.overflow = previousShellOverflow || "";
        shell.style.overflowX = previousShellOverflowX || "";
        shell.style.overflowY = previousShellOverflowY || "";
        shell.style.overscrollBehavior = previousShellOverscroll || "";
      }
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen || typeof window === "undefined") return undefined;

    const shell =
      fullscreenRootRef.current?.closest?.(".app-shell-private") ||
      document.querySelector(".app-shell-private");
    let frameId = 0;

    const updateFrame = () => {
      frameId = 0;
      const nextFrame = measureFullscreenFrame();
      setFullscreenFrame((previous) => {
        if (
          previous &&
          nextFrame &&
          previous.top === nextFrame.top &&
          previous.left === nextFrame.left &&
          previous.width === nextFrame.width &&
          previous.height === nextFrame.height &&
          previous.borderRadius === nextFrame.borderRadius
        ) {
          return previous;
        }
        return nextFrame;
      });
    };

    const scheduleFrameUpdate = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(updateFrame);
    };

    scheduleFrameUpdate();

    let resizeObserver = null;
    if (shell && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleFrameUpdate);
      resizeObserver.observe(shell);
    }

    const visualViewport = window.visualViewport;
    window.addEventListener("resize", scheduleFrameUpdate);
    window.addEventListener("orientationchange", scheduleFrameUpdate);
    visualViewport?.addEventListener("resize", scheduleFrameUpdate);
    visualViewport?.addEventListener("scroll", scheduleFrameUpdate);
    shell?.addEventListener("transitionend", scheduleFrameUpdate);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleFrameUpdate);
      window.removeEventListener("orientationchange", scheduleFrameUpdate);
      visualViewport?.removeEventListener("resize", scheduleFrameUpdate);
      visualViewport?.removeEventListener("scroll", scheduleFrameUpdate);
      shell?.removeEventListener("transitionend", scheduleFrameUpdate);
    };
  }, [isFullscreen, measureFullscreenFrame]);

  useEffect(() => {
    if (!isFullscreen || typeof window === "undefined") return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        exitFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [exitFullscreen, isFullscreen]);

  useEffect(() => {
    if (
      isFullscreen ||
      typeof window === "undefined" ||
      !restoreScrollOnFullscreenExitRef.current
    ) {
      return undefined;
    }

    restoreScrollOnFullscreenExitRef.current = false;
    const snapshot = fullscreenScrollSnapshotRef.current;
    let frameId = 0;

    const restoreChartViewport = () => {
      const shell =
        fullscreenRootRef.current?.closest?.(".app-shell-private") ||
        document.querySelector(".app-shell-private");

      if (snapshot) {
        window.scrollTo({
          left: snapshot.windowX ?? window.scrollX,
          top: snapshot.windowY ?? window.scrollY,
          behavior: "auto",
        });

        if (shell && snapshot.shellScrollTop != null) {
          shell.scrollLeft = snapshot.shellScrollLeft ?? shell.scrollLeft;
          shell.scrollTop = snapshot.shellScrollTop;
        }
      }

      fullscreenRootRef.current?.focus({ preventScroll: true });
      fullscreenRootRef.current?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "auto",
      });
    };

    frameId = window.requestAnimationFrame(() => {
      frameId = window.requestAnimationFrame(restoreChartViewport);
    });

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [isFullscreen]);

  useEffect(() => {
    candleSeriesRef.current?.applyOptions({
      upColor: CHART_COLORS.emerald,
      downColor: CHART_COLORS.rose,
      wickUpColor: CHART_COLORS.emerald,
      wickDownColor: CHART_COLORS.rose,
    });
    areaSeriesRef.current?.applyOptions(buildMainAreaSeriesOptions());
  }, [chartBarSpacing, formatAxisTick]);

  useEffect(() => {
    if (
      replayActive ||
      (!candleSeriesRef.current && !areaSeriesRef.current) ||
      initialLoaded.current ||
      candles.length === 0
    )
      return;

    debugChart(`Setting initial chart data with ${candles.length} candles`);
    candleSeriesRef.current?.setData(candles);
    areaSeriesRef.current?.setData(toAreaSeriesData(candles));
    initialLoaded.current = true;
    applyViewportAction(pendingViewportActionRef.current, candles.length);
  }, [applyViewportAction, candles, replayActive, toAreaSeriesData]);

  useEffect(() => {
    if (!replayActive || (!candleSeriesRef.current && !areaSeriesRef.current)) return;

    candleSeriesRef.current?.setData(chartCandles);
    areaSeriesRef.current?.setData(toAreaSeriesData(chartCandles));
  }, [chartCandles, replayActive, toAreaSeriesData]);

  useEffect(() => {
    if (!chartRef.current || chartCandles.length === 0) return;

    const nextValueMaps = {};

    ["sma20", "sma50", "ema12", "ema26"].forEach((key) => {
      const config = indicatorSettings[key];
      const meta = indicatorMeta[key];
      if (!config || !meta) return;

      const existingSeries = indicatorSeriesRefs.current[key];

      if (!config.enabled) {
        if (existingSeries) {
          chartRef.current.removeSeries(existingSeries);
          delete indicatorSeriesRefs.current[key];
        }
        return;
      }

      const period = normalizePeriod(config.period, meta.defaultPeriod);
      const indicatorData = meta.calc(chartCandles, period);
      const title = `${meta.label} ${period}`;
      nextValueMaps[key] = new Map(indicatorData.map((point) => [point.time, point.value]));

      if (!existingSeries) {
        indicatorSeriesRefs.current[key] = addLineSeries(chartRef.current, {
          color: config.color,
          lineWidth: config.lineWidth,
          title,
          priceLineVisible: false,
          lastValueVisible: true,
        });
      } else {
        existingSeries.applyOptions({
          color: config.color,
          lineWidth: config.lineWidth,
          title,
        });
      }

      indicatorSeriesRefs.current[key]?.setData(indicatorData);
    });

    const bbConfig = indicatorSettings.bb;
    const bbKeys = ["bbUpper", "bbMiddle", "bbLower", "bbFillUpper", "bbFillLower"];
    if (!bbConfig?.enabled) {
      bbKeys.forEach((key) => {
        if (indicatorSeriesRefs.current[key]) {
          chartRef.current.removeSeries(indicatorSeriesRefs.current[key]);
          delete indicatorSeriesRefs.current[key];
        }
      });
    } else {
      const bbPeriod = normalizePeriod(bbConfig.period, 20);
      const bbStd = normalizeStdDev(bbConfig.stdDev);
      const { upper, middle, lower } = calculateBollingerBands(chartCandles, bbPeriod, bbStd);
      nextValueMaps.bbUpper = new Map(upper.map((point) => [point.time, point.value]));
      nextValueMaps.bbMiddle = new Map(middle.map((point) => [point.time, point.value]));
      nextValueMaps.bbLower = new Map(lower.map((point) => [point.time, point.value]));

      if (!indicatorSeriesRefs.current.bbUpper) {
        indicatorSeriesRefs.current.bbFillUpper = addAreaSeries(chartRef.current, {
          lineColor: "rgba(0, 0, 0, 0)",
          topColor: withAlpha(bbConfig.color, 0.16),
          bottomColor: withAlpha(bbConfig.color, 0.06),
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        indicatorSeriesRefs.current.bbFillLower = addAreaSeries(chartRef.current, {
          lineColor: "rgba(0, 0, 0, 0)",
          topColor: CHART_COLORS.card,
          bottomColor: CHART_COLORS.card,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        indicatorSeriesRefs.current.bbUpper = addLineSeries(chartRef.current, {
          color: bbConfig.color,
          lineWidth: bbConfig.lineWidth,
          title: `BB Upper ${bbPeriod}`,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        indicatorSeriesRefs.current.bbMiddle = addLineSeries(chartRef.current, {
          color: CHART_COLORS.mutedLight,
          lineWidth: 1,
          title: `BB Mid ${bbPeriod}`,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        indicatorSeriesRefs.current.bbLower = addLineSeries(chartRef.current, {
          color: bbConfig.color,
          lineWidth: bbConfig.lineWidth,
          title: `BB Lower ${bbPeriod}`,
          priceLineVisible: false,
          lastValueVisible: true,
        });
      } else {
        indicatorSeriesRefs.current.bbFillUpper?.applyOptions({
          topColor: withAlpha(bbConfig.color, 0.16),
          bottomColor: withAlpha(bbConfig.color, 0.06),
        });
        indicatorSeriesRefs.current.bbUpper?.applyOptions({
          color: bbConfig.color,
          lineWidth: bbConfig.lineWidth,
          title: `BB Upper ${bbPeriod}`,
        });
        indicatorSeriesRefs.current.bbMiddle?.applyOptions({
          title: `BB Mid ${bbPeriod}`,
        });
        indicatorSeriesRefs.current.bbLower?.applyOptions({
          color: bbConfig.color,
          lineWidth: bbConfig.lineWidth,
          title: `BB Lower ${bbPeriod}`,
        });
      }

      indicatorSeriesRefs.current.bbFillLower?.setData(lower);
      indicatorSeriesRefs.current.bbFillUpper?.setData(upper);
      indicatorSeriesRefs.current.bbUpper?.setData(upper);
      indicatorSeriesRefs.current.bbMiddle?.setData(middle);
      indicatorSeriesRefs.current.bbLower?.setData(lower);
    }

    const vwapConfig = indicatorSettings.vwap;
    if (!vwapConfig?.enabled) {
      if (indicatorSeriesRefs.current.vwap) {
        chartRef.current.removeSeries(indicatorSeriesRefs.current.vwap);
        delete indicatorSeriesRefs.current.vwap;
      }
    } else {
      const vwapData = calculateVWAP(chartCandles);
      nextValueMaps.vwap = new Map(vwapData.map((point) => [point.time, point.value]));
      if (!indicatorSeriesRefs.current.vwap) {
        indicatorSeriesRefs.current.vwap = addLineSeries(chartRef.current, {
          color: vwapConfig.color,
          lineWidth: vwapConfig.lineWidth,
          title: "VWAP",
          priceLineVisible: false,
          lastValueVisible: true,
        });
      } else {
        indicatorSeriesRefs.current.vwap.applyOptions({
          color: vwapConfig.color,
          lineWidth: vwapConfig.lineWidth,
          title: "VWAP",
        });
      }
      indicatorSeriesRefs.current.vwap.setData(vwapData);
    }

    indicatorValueMapRef.current = {
      ...indicatorValueMapRef.current,
      ...nextValueMaps,
    };
  }, [
    chartCandles,
    indicatorSettings,
    calculateBollingerBands,
    calculateVWAP,
    indicatorMeta,
    normalizePeriod,
    normalizeStdDev,
  ]);

  useEffect(() => {
    if (!chartCandles.length || !rsiSeriesRef.current || !rsiReady) return;
    if (!indicatorSettings.rsi.enabled) {
      rsiSeriesRef.current.setData([]);
      return;
    }

    const period = normalizePeriod(indicatorSettings.rsi.period, 14);
    const rsiData = calculateRSI(chartCandles, period);
    rsiSeriesRef.current.applyOptions({
      color: indicatorSettings.rsi.color,
      lineWidth: indicatorSettings.rsi.lineWidth,
      title: `RSI ${period}`,
    });
    rsiSeriesRef.current.setData(padIndicatorData(rsiData, chartCandles));
    indicatorValueMapRef.current = {
      ...indicatorValueMapRef.current,
      rsi: new Map(rsiData.map((point) => [point.time, point.value])),
    };
  }, [chartCandles, indicatorSettings.rsi, rsiReady, calculateRSI, normalizePeriod]);

  useEffect(() => {
    if (!chartCandles.length || !macdSeriesRefs.current.macd || !macdReady) return;
    if (!indicatorSettings.macd.enabled) {
      macdSeriesRefs.current.macd?.setData([]);
      macdSeriesRefs.current.signal?.setData([]);
      macdSeriesRefs.current.hist?.setData([]);
      return;
    }

    const normalized = normalizeMacdSettings(indicatorSettings.macd);
    const macdData = calculateMACD(
      chartCandles,
      Number.parseInt(normalized.fast, 10),
      Number.parseInt(normalized.slow, 10),
      Number.parseInt(normalized.signal, 10),
    );

    macdSeriesRefs.current.macd.applyOptions({
      color: indicatorSettings.macd.color,
      lineWidth: indicatorSettings.macd.lineWidth,
      title: `MACD ${normalized.fast}/${normalized.slow}`,
    });
    macdSeriesRefs.current.signal.applyOptions({
      color: indicatorSettings.macd.signalColor,
      lineWidth: 1,
      title: `Signal ${normalized.signal}`,
    });

    const histogramData = macdData.hist.map((point) => ({
      time: point.time,
      value: point.value,
      color:
        point.value >= 0
          ? indicatorSettings.macd.histUpColor
          : indicatorSettings.macd.histDownColor,
    }));

    macdSeriesRefs.current.macd.setData(padIndicatorData(macdData.macd, chartCandles));
    macdSeriesRefs.current.signal.setData(padIndicatorData(macdData.signal, chartCandles));
    macdSeriesRefs.current.hist.setData(padIndicatorData(histogramData, chartCandles));

    indicatorValueMapRef.current = {
      ...indicatorValueMapRef.current,
      macd: new Map(macdData.macd.map((point) => [point.time, point.value])),
      macdSignal: new Map(macdData.signal.map((point) => [point.time, point.value])),
      macdHist: new Map(macdData.hist.map((point) => [point.time, point.value])),
    };
  }, [chartCandles, indicatorSettings.macd, macdReady, calculateMACD, normalizeMacdSettings]);

  useEffect(() => {
    if (!chartCandles.length || !atrSeriesRef.current || !atrReady) return;
    if (!indicatorSettings.atr.enabled) {
      atrSeriesRef.current.setData([]);
      return;
    }

    const period = normalizePeriod(indicatorSettings.atr.period, 14);
    const atrData = calculateATR(chartCandles, period);
    atrSeriesRef.current.applyOptions({
      color: indicatorSettings.atr.color,
      lineWidth: indicatorSettings.atr.lineWidth,
      title: `ATR ${period}`,
    });
    atrSeriesRef.current.setData(padIndicatorData(atrData, chartCandles));
    indicatorValueMapRef.current = {
      ...indicatorValueMapRef.current,
      atr: new Map(atrData.map((point) => [point.time, point.value])),
    };
  }, [chartCandles, indicatorSettings.atr, atrReady, calculateATR, normalizePeriod]);

  useEffect(() => {
    if (!chartCandles.length || !stochSeriesRefs.current.k || !stochReady) return;
    if (!indicatorSettings.stoch.enabled) {
      stochSeriesRefs.current.k?.setData([]);
      stochSeriesRefs.current.d?.setData([]);
      return;
    }

    const normalized = normalizeStochSettings(indicatorSettings.stoch);
    const stochData = calculateStochastic(
      chartCandles,
      Number.parseInt(normalized.kPeriod, 10),
      Number.parseInt(normalized.dPeriod, 10),
    );

    stochSeriesRefs.current.k.applyOptions({
      color: indicatorSettings.stoch.colorK,
      lineWidth: indicatorSettings.stoch.lineWidth,
      title: `%K ${normalized.kPeriod}`,
    });
    stochSeriesRefs.current.d.applyOptions({
      color: indicatorSettings.stoch.colorD,
      lineWidth: 1,
      title: `%D ${normalized.dPeriod}`,
    });

    stochSeriesRefs.current.k.setData(padIndicatorData(stochData.k, chartCandles));
    stochSeriesRefs.current.d.setData(padIndicatorData(stochData.d, chartCandles));

    indicatorValueMapRef.current = {
      ...indicatorValueMapRef.current,
      stochK: new Map(stochData.k.map((point) => [point.time, point.value])),
      stochD: new Map(stochData.d.map((point) => [point.time, point.value])),
    };
  }, [
    chartCandles,
    indicatorSettings.stoch,
    stochReady,
    calculateStochastic,
    normalizeStochSettings,
  ]);

  useEffect(() => {
    if (!isOldContractsMode) return;
    fetchOldContractsSymbols();
  }, [fetchOldContractsSymbols, isOldContractsMode]);

  useEffect(() => {
    candlesRef.current = [];
    lastCandleTimeRef.current = null;
    oldestCandleTimeRef.current = null;
    hasMoreDataRef.current = true;
    isFetchingOlderRef.current = false;
    oldContractsNextPageRef.current = 1;
    oldContractsPageSizeRef.current = OLD_CONTRACTS_MAX_PAGE_SIZE;
    historyLoadArmedRef.current = false;
    initialLoaded.current = false;
    wsHistoryRecoveryAttemptRef.current = false;
    if (wsHistoryRecoveryTimeoutRef.current) {
      window.clearTimeout(wsHistoryRecoveryTimeoutRef.current);
      wsHistoryRecoveryTimeoutRef.current = null;
    }
    autoFollowRef.current = true;
    visibleLogicalRangeRef.current = null;
    resetViewportOnNextLoadRef.current = true;
    pendingViewportActionRef.current = null;
    lastLoadedChartViewKeyRef.current = null;
    setCandles([]);
    setLatestCandle(null);
    setHoverInfo(null);
    indicatorValueMapRef.current = {};

    candleSeriesRef.current?.setData([]);
    areaSeriesRef.current?.setData([]);
    Object.values(indicatorSeriesRefs.current).forEach((seriesInstance) => {
      seriesInstance?.setData?.([]);
    });
    rsiSeriesRef.current?.setData([]);
    macdSeriesRefs.current.macd?.setData([]);
    macdSeriesRefs.current.signal?.setData([]);
    macdSeriesRefs.current.hist?.setData([]);
    atrSeriesRef.current?.setData([]);
    stochSeriesRefs.current.k?.setData([]);
    stochSeriesRefs.current.d?.setData([]);
  }, [currentChartViewKey]);

  useEffect(() => {
    const viewportMode = resetViewportOnNextLoadRef.current
      ? VIEWPORT_MODE_RESET
      : VIEWPORT_MODE_SMART_PRESERVE;
    fetchCandles({ viewportMode });
  }, [fetchCandles]);

  useEffect(() => {
    if (!accountSetId || !currentSet || isOldContractsMode) return undefined;

    const scheduleHistoryRecovery = (message) => {
      const payload = message?.data ?? message;
      const incomingAccountSetId = message?.accountSetId ?? payload?.accountSetId;
      if (incomingAccountSetId && String(incomingAccountSetId) !== String(accountSetId)) return;
      if (wsHistoryRecoveryAttemptRef.current) return;
      if (replayActiveRef.current) return;
      if (candlesRef.current.length > 2) return;

      wsHistoryRecoveryAttemptRef.current = true;
      if (wsHistoryRecoveryTimeoutRef.current) {
        window.clearTimeout(wsHistoryRecoveryTimeoutRef.current);
      }

      wsHistoryRecoveryTimeoutRef.current = window.setTimeout(() => {
        wsHistoryRecoveryTimeoutRef.current = null;
        fetchCandles({ viewportMode: VIEWPORT_MODE_SMART_PRESERVE });
      }, 250);
    };

    const unsubscribeConnection = onMessage("connection", scheduleHistoryRecovery);
    const unsubscribeSubscription = onMessage("subscription_confirmed", scheduleHistoryRecovery);

    return () => {
      unsubscribeConnection?.();
      unsubscribeSubscription?.();
      if (wsHistoryRecoveryTimeoutRef.current) {
        window.clearTimeout(wsHistoryRecoveryTimeoutRef.current);
        wsHistoryRecoveryTimeoutRef.current = null;
      }
    };
  }, [accountSetId, currentSet, fetchCandles, isOldContractsMode]);

  useEffect(() => {
    if (!accountSetId || !currentSet || isOldContractsMode) {
      setTpPausedMeta(null);
      return;
    }

    debugChart("Setting up WebSocket connection for premium updates...");
    debugChart(
      `Account: ${accountSetId}, Symbols: ${currentSet.futureSymbol} vs ${currentSet.spotSymbol}`,
    );

    setTpPausedMeta(null);

    const handlePremiumUpdate = (data) => {
      if (String(data.accountSetId) === String(accountSetId)) {
        setTpPausedMeta(null);
        handleRealtimeUpdate(data);
      }
    };

    const unsubscribe = onMessage("premium_update", handlePremiumUpdate);
    const unsubscribeError = onMessage("error", (msg) => {
      const payload = msg?.data ?? msg;
      const incomingAccountSetId = msg?.accountSetId ?? payload?.accountSetId;
      if (incomingAccountSetId && String(incomingAccountSetId) !== String(accountSetId)) return;

      const status = String(payload?.status || "").toLowerCase();
      const marketClosed = Boolean(payload?.marketClosed);
      const lastKnownPremium = Number(payload?.lastKnownPremium);
      const lastKnownProfit = Number(payload?.lastKnownProfit);
      const hasLastKnownPremium = Number.isFinite(lastKnownPremium);
      const hasLastKnownProfit = Number.isFinite(lastKnownProfit);

      if (status !== "paused" && !marketClosed && !hasLastKnownPremium && !hasLastKnownProfit)
        return;

      setTpPausedMeta({
        message: String(payload?.message || ""),
        status,
        reason: String(payload?.reason || "").toLowerCase(),
        marketClosed,
        lastKnownPremium: hasLastKnownPremium ? lastKnownPremium : null,
        lastKnownProfit: hasLastKnownProfit ? lastKnownProfit : null,
        lastKnownAt: payload?.lastKnownAt || "",
        lastKnownProfitAt: payload?.lastKnownProfitAt || "",
      });
    });

    connectWS(accountSetId);
    subscribeToPremium(accountSetId, currentSet.futureSymbol, currentSet.spotSymbol);

    return () => {
      unsubscribe?.();
      unsubscribeError?.();
    };
  }, [accountSetId, currentSet, handleRealtimeUpdate, isOldContractsMode]);
  const adjustZoom = useCallback(
    (zoomFactor) => {
      if (!chartRef.current || chartCandles.length === 0) return;

      const timeScale = chartRef.current.timeScale();
      const currentRange = visibleLogicalRangeRef.current ?? timeScale.getVisibleLogicalRange();

      if (!currentRange) {
        runProgrammaticTimeScaleUpdate(
          () => {
            timeScale.fitContent();
          },
          { enableAutoFollow: true },
        );
        return;
      }

      const center = (currentRange.from + currentRange.to) / 2;
      const currentSpan = Math.max(1, currentRange.to - currentRange.from);
      const nextRange = clampLogicalRange(
        {
          from: center - (currentSpan * zoomFactor) / 2,
          to: center + (currentSpan * zoomFactor) / 2,
        },
        chartCandles.length,
      );

      if (!nextRange) return;

      runProgrammaticTimeScaleUpdate(() => {
        timeScale.setVisibleLogicalRange(nextRange);
      });
    },
    [chartCandles.length, clampLogicalRange, runProgrammaticTimeScaleUpdate],
  );

  const handleZoomIn = useCallback(() => {
    adjustZoom(0.8);
  }, [adjustZoom]);

  const handleZoomOut = useCallback(() => {
    adjustZoom(1.25);
  }, [adjustZoom]);

  const focusReplayViewport = useCallback(
    (barsCount = chartCandles.length, { resetPriceScale = false } = {}) => {
      if (!chartRef.current || barsCount <= 0) return;

      const replayWindow = isMobileViewport ? 28 : 56;
      const preferredRange =
        barsCount > replayWindow
          ? { from: barsCount - replayWindow - 0.5, to: barsCount - 0.5 }
          : { from: -0.5, to: barsCount - 0.5 };
      const nextRange = clampLogicalRange(preferredRange, barsCount);

      if (!nextRange) return;

      runProgrammaticTimeScaleUpdate(
        () => {
          chartRef.current?.timeScale().setVisibleLogicalRange(nextRange);
        },
        { enableAutoFollow: false },
      );
      if (resetPriceScale) {
        resetAllPriceScalesToAuto();
      }
    },
    [
      chartCandles.length,
      clampLogicalRange,
      isMobileViewport,
      resetAllPriceScalesToAuto,
      runProgrammaticTimeScaleUpdate,
    ],
  );

  const handleReset = useCallback(() => {
    historyLoadArmedRef.current = false;
    if (replayActive) {
      focusReplayViewport(chartCandles.length, { resetPriceScale: true });
      return;
    }
    applyResetViewport(candles.length);
  }, [applyResetViewport, candles.length, chartCandles.length, focusReplayViewport, replayActive]);

  const startReplay = useCallback(() => {
    if (candles.length < 2) {
      toast.warning("Replay needs at least 2 candles.");
      return;
    }

    historyLoadArmedRef.current = false;
    autoFollowRef.current = false;
    setHoverInfo(null);
    setReplayActive(true);
    setReplayPlaying(false);
    setReplayCursor(1);
  }, [candles.length, toast]);

  const exitReplay = useCallback(() => {
    candleSeriesRef.current?.setData(candles);
    areaSeriesRef.current?.setData(toAreaSeriesData(candles));
    setReplayPlaying(false);
    setReplayActive(false);
    setReplayCursor(1);
    setHoverInfo(null);
    historyLoadArmedRef.current = false;
    if (candles.length > 0) {
      applyResetViewport(candles.length);
    }
  }, [applyResetViewport, candles, toAreaSeriesData]);

  const toggleReplayPlayback = useCallback(() => {
    if (!replayActive) {
      startReplay();
      return;
    }

    if (replayVisibleCount >= candles.length) {
      setReplayCursor(1);
    }
    setReplayPlaying((previous) => !previous);
  }, [candles.length, replayActive, replayVisibleCount, startReplay]);

  const restartReplay = useCallback(() => {
    if (candles.length < 2) return;
    historyLoadArmedRef.current = false;
    setHoverInfo(null);
    setReplayActive(true);
    setReplayPlaying(false);
    setReplayCursor(1);
  }, [candles.length]);

  const handleReplayScrub = useCallback(
    (value) => {
      if (candles.length === 0) return;

      const nextCursor = Math.min(Math.max(Number(value) || 1, 1), candles.length);
      historyLoadArmedRef.current = false;
      setHoverInfo(null);
      setReplayActive(true);
      setReplayPlaying(false);
      setReplayCursor(nextCursor);
    },
    [candles.length],
  );

  useEffect(() => {
    if (!replayActive) return;

    setHoverInfo(null);
    focusReplayViewport(chartCandles.length);
  }, [chartCandles.length, focusReplayViewport, replayActive]);

  const toggleIndicator = (indicatorName) => {
    setIndicatorSettings((prev) => {
      const current = prev[indicatorName] || {};
      const enabled = !current.enabled;
      const next = {
        ...prev,
        [indicatorName]: { ...current, enabled },
      };

      if (!enabled) {
        if (indicatorName === "bb") {
          ["bbUpper", "bbMiddle", "bbLower"].forEach((key) => {
            if (indicatorSeriesRefs.current[key]) {
              chartRef.current?.removeSeries(indicatorSeriesRefs.current[key]);
              delete indicatorSeriesRefs.current[key];
            }
          });
        } else if (indicatorName === "vwap") {
          if (indicatorSeriesRefs.current.vwap) {
            chartRef.current?.removeSeries(indicatorSeriesRefs.current.vwap);
            delete indicatorSeriesRefs.current.vwap;
          }
        } else if (indicatorSeriesRefs.current[indicatorName]) {
          chartRef.current?.removeSeries(indicatorSeriesRefs.current[indicatorName]);
          delete indicatorSeriesRefs.current[indicatorName];
        }
      }

      return next;
    });
  };

  const updateIndicatorField = (indicatorName, field, value) => {
    setIndicatorSettings((prev) => ({
      ...prev,
      [indicatorName]: { ...prev[indicatorName], [field]: value },
    }));
  };
  const normalizeIndicatorPeriod = (indicatorName) => {
    setIndicatorSettings((prev) => {
      const meta = indicatorMeta[indicatorName];
      const fallback = meta?.defaultPeriod || 14;
      const normalized = normalizePeriod(prev[indicatorName]?.period, fallback);
      return {
        ...prev,
        [indicatorName]: { ...prev[indicatorName], period: String(normalized) },
      };
    });
  };

  const exportChartData = async () => {
    if (!chartRef.current || !candles.length) {
      toast.warning("Chart image is not ready yet.");
      return;
    }

    try {
      const sections = [
        {
          key: "main",
          label: "Premium Chart",
          canvas: chartRef.current.takeScreenshot(),
        },
      ];

      if (indicatorSettings.rsi.enabled && rsiChartRef.current) {
        sections.push({
          key: "rsi",
          label: `RSI (${indicatorSettings.rsi.period})`,
          canvas: rsiChartRef.current.takeScreenshot(),
        });
      }

      if (indicatorSettings.macd.enabled && macdChartRef.current) {
        sections.push({
          key: "macd",
          label: `MACD (${indicatorSettings.macd.fast}, ${indicatorSettings.macd.slow}, ${indicatorSettings.macd.signal})`,
          canvas: macdChartRef.current.takeScreenshot(),
        });
      }

      if (indicatorSettings.atr.enabled && atrChartRef.current) {
        sections.push({
          key: "atr",
          label: `ATR (${indicatorSettings.atr.period})`,
          canvas: atrChartRef.current.takeScreenshot(),
        });
      }

      if (indicatorSettings.stoch.enabled && stochChartRef.current) {
        sections.push({
          key: "stoch",
          label: `Stochastic (${indicatorSettings.stoch.kPeriod}, ${indicatorSettings.stoch.dPeriod})`,
          canvas: stochChartRef.current.takeScreenshot(),
        });
      }

      const exportSections = sections.filter((section) => section.canvas);
      if (!exportSections.length) {
        toast.error("Failed to capture chart image.");
        return;
      }

      const pixelRatio =
        typeof window !== "undefined" ? Math.max(window.devicePixelRatio || 1, 1) : 1;
      const screenshotWidth = Math.max(...exportSections.map((section) => section.canvas.width));
      const horizontalPadding = Math.round(24 * pixelRatio);
      const topPadding = Math.round(22 * pixelRatio);
      const bottomPadding = Math.round(18 * pixelRatio);
      const headerHeight = Math.round(54 * pixelRatio);
      const sectionGap = Math.round(12 * pixelRatio);
      const labelGap = Math.round(8 * pixelRatio);
      const labelHeight = Math.round(18 * pixelRatio);

      const exportHeight = exportSections.reduce(
        (total, section, index) => {
          const nextTotal =
            total + section.canvas.height + (index === 0 ? 0 : labelHeight + labelGap);
          return nextTotal + (index === exportSections.length - 1 ? 0 : sectionGap);
        },
        topPadding + headerHeight + bottomPadding,
      );

      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = screenshotWidth + horizontalPadding * 2;
      exportCanvas.height = exportHeight;

      const ctx = exportCanvas.getContext("2d");
      if (!ctx) {
        toast.error("Failed to prepare chart image.");
        return;
      }

      ctx.fillStyle = CHART_COLORS.card;
      ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

      ctx.fillStyle = CHART_COLORS.fg;
      ctx.font = `${Math.round(20 * pixelRatio)}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = "top";
      ctx.fillText(company || "Premium Chart", horizontalPadding, topPadding);

      ctx.fillStyle = CHART_COLORS.mutedLight;
      ctx.font = `${Math.round(11 * pixelRatio)}px Inter, system-ui, sans-serif`;
      const exportMeta = `${isOldContractsMode ? `Old Contracts • ${premiumChartSideLabel}` : `Live Premium • ${premiumChartSideLabel}`} • ${tf}m • ${new Date().toLocaleString(
        "en-GB",
        {
          hour12: false,
        },
      )}`;
      ctx.fillText(exportMeta, horizontalPadding, topPadding + Math.round(26 * pixelRatio));

      let currentY = topPadding + headerHeight;
      exportSections.forEach((section, index) => {
        if (index > 0) {
          ctx.fillStyle = CHART_COLORS.mutedLight;
          ctx.font = `${Math.round(11 * pixelRatio)}px Inter, system-ui, sans-serif`;
          ctx.fillText(section.label, horizontalPadding, currentY);
          currentY += labelHeight + labelGap;
        }

        const sectionX =
          horizontalPadding + Math.round((screenshotWidth - section.canvas.width) / 2);
        ctx.drawImage(section.canvas, sectionX, currentY);
        currentY += section.canvas.height + sectionGap;
      });

      const exportDate = new Date().toISOString().replace(/[:.]/g, "-");
      const exportName = `premium-chart-${sanitizeExportSegment(company, "market")}-${sanitizeExportSegment(isOldContractsMode ? `old-contracts-${premiumChartSide}` : premiumChartSide, "sell")}-${sanitizeExportSegment(tf, "1")}m-${exportDate}.png`;
      await downloadCanvasAsPng(exportCanvas, exportName);
      toast.success("Chart image downloaded.");
    } catch (error) {
      console.error("Failed to export chart image:", error);
      toast.error("Failed to export chart image.");
    }
  };

  const lastCandleForDisplay = chartCandles[chartCandles.length - 1] || null;
  const hasLiveRealtimeCandle = Boolean(latestCandle?.isRealTime);
  const historicalCandleCount = Math.max(candles.length - (hasLiveRealtimeCandle ? 1 : 0), 0);
  const replayHasLiveRealtimeCandle = replayActive && Boolean(lastCandleForDisplay?.isRealTime);
  const replayHistoricalCandleCount = Math.max(
    chartCandles.length - (replayHasLiveRealtimeCandle ? 1 : 0),
    0,
  );
  const candleCountLabel = replayActive
    ? `${replayHistoricalCandleCount}/${historicalCandleCount}${hasLiveRealtimeCandle ? " + Live" : ""}`
    : `${historicalCandleCount}${hasLiveRealtimeCandle ? " + Live" : ""}`;
  const replayProgress =
    candles.length > 0 ? Math.round((replayVisibleCount / candles.length) * 100) : 0;
  const replayProgressRatio =
    candles.length > 1 ? ((replayVisibleCount - 1) / (candles.length - 1)) * 100 : 0;
  const displayTimeKey = hoverInfo?.time ?? lastCandleForDisplay?.time ?? null;
  const displayCandle = hoverInfo
    ? hoverInfo
    : lastCandleForDisplay
      ? {
          time: lastCandleForDisplay.time,
          open: lastCandleForDisplay.open,
          high: lastCandleForDisplay.high,
          low: lastCandleForDisplay.low,
          close: lastCandleForDisplay.close,
        }
      : null;

  const indicatorReadouts = [];
  if (indicatorSettings.sma20.enabled) {
    indicatorReadouts.push({
      label: `SMA ${indicatorSettings.sma20.period}`,
      value: getIndicatorValue("sma20", displayTimeKey),
    });
  }
  if (indicatorSettings.sma50.enabled) {
    indicatorReadouts.push({
      label: `SMA ${indicatorSettings.sma50.period}`,
      value: getIndicatorValue("sma50", displayTimeKey),
    });
  }
  if (indicatorSettings.ema12.enabled) {
    indicatorReadouts.push({
      label: `EMA ${indicatorSettings.ema12.period}`,
      value: getIndicatorValue("ema12", displayTimeKey),
    });
  }
  if (indicatorSettings.ema26.enabled) {
    indicatorReadouts.push({
      label: `EMA ${indicatorSettings.ema26.period}`,
      value: getIndicatorValue("ema26", displayTimeKey),
    });
  }
  if (indicatorSettings.bb.enabled) {
    indicatorReadouts.push({
      label: "BB Upper",
      value: getIndicatorValue("bbUpper", displayTimeKey),
    });
    indicatorReadouts.push({
      label: "BB Mid",
      value: getIndicatorValue("bbMiddle", displayTimeKey),
    });
    indicatorReadouts.push({
      label: "BB Lower",
      value: getIndicatorValue("bbLower", displayTimeKey),
    });
  }
  if (indicatorSettings.vwap.enabled) {
    indicatorReadouts.push({ label: "VWAP", value: getIndicatorValue("vwap", displayTimeKey) });
  }
  if (indicatorSettings.rsi.enabled) {
    indicatorReadouts.push({
      label: `RSI ${indicatorSettings.rsi.period}`,
      value: getIndicatorValue("rsi", displayTimeKey),
    });
  }
  if (indicatorSettings.atr.enabled) {
    indicatorReadouts.push({
      label: `ATR ${indicatorSettings.atr.period}`,
      value: getIndicatorValue("atr", displayTimeKey),
    });
  }
  if (indicatorSettings.stoch.enabled) {
    indicatorReadouts.push({
      label: "Stoch %K",
      value: getIndicatorValue("stochK", displayTimeKey),
    });
    indicatorReadouts.push({
      label: "Stoch %D",
      value: getIndicatorValue("stochD", displayTimeKey),
    });
  }
  if (indicatorSettings.macd.enabled) {
    indicatorReadouts.push({ label: "MACD", value: getIndicatorValue("macd", displayTimeKey) });
    indicatorReadouts.push({
      label: "Signal",
      value: getIndicatorValue("macdSignal", displayTimeKey),
    });
    indicatorReadouts.push({ label: "Hist", value: getIndicatorValue("macdHist", displayTimeKey) });
  }

  const selectedIndicatorMeta =
    INDICATOR_LIBRARY_ITEMS.find((item) => item.key === selectedIndicatorKey) ||
    INDICATOR_LIBRARY_ITEMS[0];
  const filteredLibraryItems = INDICATOR_LIBRARY_ITEMS;
  const selectedIndicatorConfig = selectedIndicatorMeta
    ? indicatorSettings[selectedIndicatorMeta.key]
    : null;
  const selectedIndicatorAccent = selectedIndicatorMeta
    ? CHART_COLORS[selectedIndicatorMeta.accentKey] || CHART_COLORS.blueLight
    : CHART_COLORS.blueLight;
  const liveSymbolPairLabel = [currentSet?.futureSymbol, currentSet?.spotSymbol]
    .filter(Boolean)
    .join(" • ");
  const activeLowerPanels = [
    indicatorSettings.rsi.enabled ? "rsi" : null,
    indicatorSettings.macd.enabled ? "macd" : null,
    indicatorSettings.atr.enabled ? "atr" : null,
    indicatorSettings.stoch.enabled ? "stoch" : null,
  ].filter(Boolean);
  const indicatorPanelHeights = {
    rsi: 140,
    macd: 160,
    atr: 140,
    stoch: 150,
  };
  const activeIndicatorPanelsHeight = activeLowerPanels.reduce(
    (total, key) => total + (indicatorPanelHeights[key] || 0),
    0,
  );
  const bottomVisiblePanel = activeLowerPanels[activeLowerPanels.length - 1] || null;
  const hasIndicatorPanels = bottomVisiblePanel !== null;
  const chartStatusBadge = (() => {
    if (replayActive) {
      return {
        label: replayPlaying ? "REPLAY" : "REPLAY",
        background: withAlpha(CHART_COLORS.blue, 0.16),
        border: withAlpha(CHART_COLORS.blueLight, 0.34),
        color: CHART_COLORS.blueLight,
      };
    }
    if (tpPausedMeta?.status === "paused") {
      return {
        label: "PAUSED",
        background: withAlpha(CHART_COLORS.amber, 0.14),
        border: withAlpha(CHART_COLORS.amberLight, 0.34),
        color: CHART_COLORS.amberLight,
      };
    }
    if (connectionStatus === "live") {
      return {
        label: "LIVE",
        background: "var(--chart-live-badge-bg)",
        border: "var(--chart-live-badge-border)",
        color: "var(--chart-live-badge-color)",
      };
    }
    if (connectionStatus === "error") {
      return {
        label: "ERROR",
        background: withAlpha(CHART_COLORS.rose, 0.14),
        border: withAlpha(CHART_COLORS.roseLight, 0.34),
        color: CHART_COLORS.roseLight,
      };
    }
    if (connectionStatus === "no-data") {
      return {
        label: "NO DATA",
        background: withAlpha(CHART_COLORS.rose, 0.12),
        border: withAlpha(CHART_COLORS.roseLight, 0.3),
        color: CHART_COLORS.roseLight,
      };
    }
    return {
      label: "STALE",
      background: withAlpha(CHART_COLORS.amber, 0.14),
      border: withAlpha(CHART_COLORS.amberLight, 0.34),
      color: CHART_COLORS.amberLight,
    };
  })();
  useEffect(() => {
    safelyRunChartOperation(() => {
      chartRef.current?.applyOptions({
        timeScale: {
          visible: !hasIndicatorPanels,
          timeVisible: !hasIndicatorPanels,
          borderVisible: !hasIndicatorPanels,
          ticksVisible: !hasIndicatorPanels,
          tickMarkFormatter: formatAxisTick,
        },
      });
    });

    const applyPanelAxisState = (chartInstance, isBottomPanel) => {
      safelyRunChartOperation(() => {
        chartInstance?.applyOptions({
          timeScale: {
            visible: isBottomPanel,
            timeVisible: isBottomPanel,
            borderVisible: isBottomPanel,
            ticksVisible: isBottomPanel,
          },
        });
      });
    };

    applyPanelAxisState(rsiChartRef.current, bottomVisiblePanel === "rsi");
    applyPanelAxisState(macdChartRef.current, bottomVisiblePanel === "macd");
    applyPanelAxisState(atrChartRef.current, bottomVisiblePanel === "atr");
    applyPanelAxisState(stochChartRef.current, bottomVisiblePanel === "stoch");
  }, [bottomVisiblePanel, formatAxisTick, hasIndicatorPanels, safelyRunChartOperation]);
  const getOhlcValueColor = (_referenceOpen, value, label) => {
    if (!Number.isFinite(value)) return CHART_COLORS.fg;
    if (label === "H") return CHART_COLORS.emeraldLight;
    if (label === "L") return CHART_COLORS.roseLight;
    return CHART_COLORS.fg;
  };
  const ohlcReadoutItems = displayCandle
    ? [
        { label: "O", value: displayCandle.open },
        { label: "H", value: displayCandle.high },
        { label: "L", value: displayCandle.low },
        { label: "C", value: displayCandle.close },
      ]
    : [];
  const selectedIndicatorFields =
    selectedIndicatorMeta && selectedIndicatorConfig
      ? (() => {
          switch (selectedIndicatorMeta.key) {
            case "bb":
              return [
                {
                  label: "Period",
                  value: selectedIndicatorConfig.period,
                  onChange: (value) => updateIndicatorField("bb", "period", value),
                  onBlur: () =>
                    updateIndicatorField(
                      "bb",
                      "period",
                      String(normalizePeriod(selectedIndicatorConfig.period, 20)),
                    ),
                },
                {
                  label: "Std Dev",
                  value: selectedIndicatorConfig.stdDev,
                  onChange: (value) => updateIndicatorField("bb", "stdDev", value),
                  onBlur: () =>
                    updateIndicatorField(
                      "bb",
                      "stdDev",
                      String(normalizeStdDev(selectedIndicatorConfig.stdDev)),
                    ),
                },
              ];
            case "vwap":
              return [];
            case "stoch":
              return [
                {
                  label: "K Length",
                  value: selectedIndicatorConfig.kPeriod,
                  onChange: (value) => updateIndicatorField("stoch", "kPeriod", value),
                  onBlur: () => {
                    const normalized = normalizeStochSettings(selectedIndicatorConfig);
                    setIndicatorSettings((prev) => ({
                      ...prev,
                      stoch: { ...prev.stoch, ...normalized },
                    }));
                  },
                },
                {
                  label: "D Length",
                  value: selectedIndicatorConfig.dPeriod,
                  onChange: (value) => updateIndicatorField("stoch", "dPeriod", value),
                  onBlur: () => {
                    const normalized = normalizeStochSettings(selectedIndicatorConfig);
                    setIndicatorSettings((prev) => ({
                      ...prev,
                      stoch: { ...prev.stoch, ...normalized },
                    }));
                  },
                },
              ];
            case "macd":
              return [
                {
                  label: "Fast",
                  value: selectedIndicatorConfig.fast,
                  onChange: (value) => updateIndicatorField("macd", "fast", value),
                  onBlur: () => {
                    const normalized = normalizeMacdSettings(selectedIndicatorConfig);
                    setIndicatorSettings((prev) => ({
                      ...prev,
                      macd: { ...prev.macd, ...normalized },
                    }));
                  },
                },
                {
                  label: "Slow",
                  value: selectedIndicatorConfig.slow,
                  onChange: (value) => updateIndicatorField("macd", "slow", value),
                  onBlur: () => {
                    const normalized = normalizeMacdSettings(selectedIndicatorConfig);
                    setIndicatorSettings((prev) => ({
                      ...prev,
                      macd: { ...prev.macd, ...normalized },
                    }));
                  },
                },
                {
                  label: "Signal",
                  value: selectedIndicatorConfig.signal,
                  onChange: (value) => updateIndicatorField("macd", "signal", value),
                  onBlur: () => {
                    const normalized = normalizeMacdSettings(selectedIndicatorConfig);
                    setIndicatorSettings((prev) => ({
                      ...prev,
                      macd: { ...prev.macd, ...normalized },
                    }));
                  },
                },
              ];
            default:
              return [
                {
                  label: "Period",
                  value: selectedIndicatorConfig.period,
                  onChange: (value) =>
                    updateIndicatorField(selectedIndicatorMeta.key, "period", value),
                  onBlur: () => normalizeIndicatorPeriod(selectedIndicatorMeta.key),
                },
              ];
          }
        })()
      : [];
  const showChartOverlayLoader = loading || fetching;
  const liveClockLabel = liveClock.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const fullscreenChartMinHeight = isMobileViewport ? 220 : 300;
  const fullscreenReservedHeight = (isMobileViewport ? 300 : 220) + activeIndicatorPanelsHeight;
  const fullscreenViewportHeight =
    fullscreenFrame?.height ||
    (typeof window !== "undefined"
      ? Math.round(window.innerHeight || document.documentElement?.clientHeight || 0)
      : 0);
  const fullscreenChartHeight = Math.max(
    fullscreenChartMinHeight,
    fullscreenViewportHeight - fullscreenReservedHeight,
  );
  const fullscreenOverlayWidth =
    fullscreenFrame?.width ||
    (typeof window !== "undefined"
      ? Math.round(window.innerWidth || document.documentElement?.clientWidth || 0)
      : 0);
  const chartPrimaryHeight = isFullscreen
    ? `${fullscreenChartHeight}px`
    : `${
        isMobileViewport ? 350 : Math.max(400, Math.min(520, Math.round(viewportWidth * 0.34)))
      }px`;
  const toolbarControlHeight = 42;
  const toolbarButtonHeight = 38;
  const toolbarBaseStyle = {
    height: `${toolbarButtonHeight}px`,
    minHeight: `${toolbarButtonHeight}px`,
    borderRadius: "9px",
    border: `1px solid var(--toolbar-button-border, ${CHART_COLORS.borderStrong})`,
    background: `var(--toolbar-button-bg, ${CHART_COLORS.input})`,
    color: `var(--toolbar-button-color, ${CHART_COLORS.fgDim})`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    transition:
      "background 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease",
  };
  const isLightTheme = isLightThemeActive();
  const buildFlipToggleButtonStyle = (accent, textColor = "#ffffff") => ({
    ...toolbarBaseStyle,
    height: `${toolbarControlHeight}px`,
    minHeight: `${toolbarControlHeight}px`,
    padding: isMobileViewport ? "0 14px" : "0 16px",
    borderRadius: 0,
    border: "none",
    background: accent,
    color: textColor,
    boxShadow: `inset 0 1px 0 ${withAlpha(CHART_COLORS.fg, isLightTheme ? 0.15 : 0.18)}`,
    transition: "none",
  });

  const metricLabelStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: CHART_COLORS.mutedLight,
  };
  const indicatorInputStyle = {
    minHeight: "34px",
    minWidth: "78px",
    background: CHART_COLORS.input,
    color: CHART_COLORS.fgDim,
    border: `1px solid ${CHART_COLORS.border}`,
    borderRadius: "8px",
    padding: "0 10px",
    fontSize: "12px",
    fontVariantNumeric: "tabular-nums",
  };
  const subPanelLabelStyle = {
    position: "absolute",
    top: "8px",
    left: "12px",
    zIndex: 4,
    display: "flex",
    gap: "10px",
    alignItems: "center",
    color: CHART_COLORS.mutedLight,
    fontSize: "11px",
    pointerEvents: "none",
  };
  const chartSideToolbarButtons = [
    {
      key: "indicator",
      label: "Indicators",
      title: "Indicators",
      group: "features",
      primary: true,
      active: indicatorPanelOpen,
      background: indicatorPanelOpen ? "rgba(129, 140, 248, 0.18)" : "rgba(129, 140, 248, 0.08)",
      borderColor: indicatorPanelOpen ? "rgba(129, 140, 248, 0.4)" : "rgba(129, 140, 248, 0.2)",
      textColor: indicatorPanelOpen ? "#a5b4fc" : "#818cf8",
      hoverBackground: "rgba(129, 140, 248, 0.22)",
      hoverBorder: "rgba(129, 140, 248, 0.5)",
      hoverText: "#a5b4fc",
      activeBackground: "rgba(129, 140, 248, 0.28)",
      activeBorder: "rgba(129, 140, 248, 0.55)",
      activeText: "#c7d2fe",
      onClick: () => setIndicatorPanelOpen((prev) => !prev),
      icon: <Layers3 size={isMobileViewport ? 14 : 16} />,
    },
    {
      key: "replay",
      label: replayActive ? (replayPlaying ? "Pause" : "Replay") : "Replay",
      title: replayActive ? (replayPlaying ? "Pause Replay" : "Resume Replay") : "Replay Mode",
      group: "features",
      active: replayActive,
      emerald: true,
      background: replayActive ? "rgba(45, 212, 191, 0.18)" : "rgba(45, 212, 191, 0.08)",
      borderColor: replayActive ? "rgba(45, 212, 191, 0.4)" : "rgba(45, 212, 191, 0.2)",
      textColor: replayActive ? "#5eead4" : "#2dd4bf",
      hoverBackground: "rgba(45, 212, 191, 0.22)",
      hoverBorder: "rgba(45, 212, 191, 0.5)",
      hoverText: "#5eead4",
      activeBackground: "rgba(45, 212, 191, 0.28)",
      activeBorder: "rgba(45, 212, 191, 0.55)",
      activeText: "#99f6e4",
      onClick: toggleReplayPlayback,
      icon:
        replayActive && replayPlaying ? (
          <Pause size={isMobileViewport ? 14 : 16} />
        ) : (
          <Play size={isMobileViewport ? 14 : 16} />
        ),
    },
    {
      key: "chart-type",
      label: chartStyle === "candles" ? "Candles" : "Area",
      title: `Switch chart type to ${chartStyle === "candles" ? "Area" : "Candles"}`,
      group: "features",
      secondary: true,
      active: false,
      background:
        chartStyle === "candles" ? "rgba(52, 211, 153, 0.15)" : "rgba(96, 165, 250, 0.15)",
      borderColor:
        chartStyle === "candles" ? "rgba(52, 211, 153, 0.35)" : "rgba(96, 165, 250, 0.35)",
      textColor: chartStyle === "candles" ? "#34d399" : "#60a5fa",
      hoverBackground:
        chartStyle === "candles" ? "rgba(52, 211, 153, 0.2)" : "rgba(96, 165, 250, 0.2)",
      hoverBorder:
        chartStyle === "candles" ? "rgba(52, 211, 153, 0.45)" : "rgba(96, 165, 250, 0.45)",
      hoverText: chartStyle === "candles" ? "#6ee7b7" : "#93c5fd",
      activeBackground:
        chartStyle === "candles" ? "rgba(52, 211, 153, 0.25)" : "rgba(96, 165, 250, 0.25)",
      activeBorder:
        chartStyle === "candles" ? "rgba(52, 211, 153, 0.5)" : "rgba(96, 165, 250, 0.5)",
      activeText: chartStyle === "candles" ? "#6ee7b7" : "#93c5fd",
      onClick: cycleChartStyle,
      icon: <ChartCandlestick size={isMobileViewport ? 14 : 16} />,
    },
    {
      key: "orders",
      label: "Orders",
      title: ordersOverlayEnabled ? "Hide Orders Overlay" : "Show Orders Overlay",
      group: "features",
      secondary: true,
      active: ordersOverlayEnabled,
      background: ordersOverlayEnabled ? "rgba(251, 146, 60, 0.15)" : undefined,
      borderColor: "transparent",
      textColor: ordersOverlayEnabled ? (isLightTheme ? "#c2410c" : "#fb923c") : undefined,
      hoverBackground: isLightTheme ? "rgba(251, 146, 60, 0.14)" : "rgba(251, 146, 60, 0.2)",
      hoverBorder: "transparent",
      hoverText: isLightTheme ? "#9a3412" : "#fdba74",
      activeBackground: isLightTheme ? "rgba(251, 146, 60, 0.18)" : "rgba(251, 146, 60, 0.25)",
      activeBorder: "transparent",
      activeText: isLightTheme ? "#9a3412" : "#fdba74",
      onClick: () => setOrdersOverlayEnabled((previous) => !previous),
      icon: <Activity size={isMobileViewport ? 14 : 16} />,
    },
    {
      key: "export",
      label: "Download",
      title: "Export Chart Image",
      group: "features",
      active: false,
      secondary: true,
      onClick: exportChartData,
      icon: <Download size={isMobileViewport ? 14 : 16} />,
    },
    {
      key: "zoom-in",
      title: "Zoom In",
      group: "features",
      floatingGroup: "chart-bottom",
      active: false,
      secondary: true,
      onClick: handleZoomIn,
      icon: <ZoomIn size={isMobileViewport ? 14 : 16} />,
    },
    {
      key: "zoom-out",
      title: "Zoom Out",
      group: "features",
      floatingGroup: "chart-bottom",
      active: false,
      secondary: true,
      onClick: handleZoomOut,
      icon: <ZoomOut size={isMobileViewport ? 14 : 16} />,
    },
    {
      key: "reset",
      title: "Reset View",
      group: "features",
      floatingGroup: "chart-bottom",
      active: false,
      secondary: true,
      onClick: handleReset,
      icon: <RefreshCw size={isMobileViewport ? 14 : 16} />,
    },
    {
      key: "fullscreen",
      label: isFullscreen ? "Minimize" : "Maximize",
      title: isFullscreen ? "Minimize" : "Maximize",
      group: "features",
      active: false,
      secondary: true,
      onClick: toggleFullscreen,
      icon: isFullscreen ? (
        <Minimize size={isMobileViewport ? 14 : 16} />
      ) : (
        <Maximize size={isMobileViewport ? 14 : 16} />
      ),
    },
  ];
  const oldSymbolSelector = isOldContractsMode ? (
    <div
      style={{
        width: mobileTopControlWidth
          ? `${mobileTopControlWidth}px`
          : isMobileViewport
            ? "114px"
            : "190px",
        maxWidth: mobileTopControlWidth
          ? `${mobileTopControlWidth}px`
          : isMobileViewport
            ? "114px"
            : "190px",
      }}
    >
      <CustomSelect
        value={selectedOldSymbol}
        onChange={(value) => {
          setSelectedOldSymbol(value);
          initialLoaded.current = false;
        }}
        options={oldSymbolOptions}
        placeholder={oldContractsPlaceholder}
        disabled={oldContractsSelectDisabled}
        searchable={false}
        className={`chart-toolbar-select${isMobileViewport ? " chart-toolbar-select-mobile" : ""}`}
        dropdownClassName="chart-toolbar-select-dropdown"
      />
      {oldContractsHelperText && (
        <div
          style={{
            marginTop: "6px",
            color: CHART_COLORS.mutedLight,
            fontSize: "11px",
            lineHeight: 1.45,
          }}
        >
          {oldContractsHelperText}
        </div>
      )}
    </div>
  ) : null;
  const modeToggleIcon = isOldContractsMode ? Clock3 : Activity;
  const modeToggleLabel = isOldContractsMode ? "Old" : "Live";
  const modeToggleAccent = isOldContractsMode ? CHART_COLORS.fgDim : CHART_COLORS.blue;
  const modeToggleTextColor = isOldContractsMode
    ? isLightTheme
      ? "#ffffff"
      : CHART_COLORS.card
    : "#ffffff";
  const ModeToggleIcon = modeToggleIcon;
  const modeToggle = (
    <button
      type="button"
      onClick={() => setIsOldContractsMode((previous) => !previous)}
      className="chart-flip-toggle-button"
      style={{
        ...buildFlipToggleButtonStyle(modeToggleAccent, modeToggleTextColor),
        "--chart-flip-toggle-hover-bg": withAlpha(modeToggleAccent, isLightTheme ? 0.92 : 0.9),
        "--chart-flip-toggle-focus-border": withAlpha(modeToggleAccent, isLightTheme ? 0.42 : 0.52),
        "--chart-flip-toggle-focus-ring": withAlpha(modeToggleAccent, isLightTheme ? 0.16 : 0.24),
      }}
      aria-pressed={isOldContractsMode}
      title={
        isOldContractsMode
          ? "Old Contracts. Tap to switch to Live Premium"
          : "Live Premium. Tap to switch to Old Contracts"
      }
      aria-label={
        isOldContractsMode
          ? "Old Contracts. Tap to switch to Live Premium"
          : "Live Premium. Tap to switch to Old Contracts"
      }
    >
      <ModeToggleIcon className="chart-flip-toggle-icon" size={15} />
      <span>{modeToggleLabel}</span>
    </button>
  );
  const premiumSideIsBuy = premiumChartSide === "buy";
  const premiumSideAccent = premiumSideIsBuy ? CHART_COLORS.emerald : CHART_COLORS.rose;
  const premiumSideLabel = premiumSideIsBuy ? "Buy" : "Sell";
  const PremiumSideIcon = premiumSideIsBuy ? ArrowUpRight : ArrowDownRight;
  const premiumSideToggle = (
    <button
      type="button"
      onClick={() => setPremiumChartSide((previous) => (previous === "buy" ? "sell" : "buy"))}
      className="chart-flip-toggle-button"
      style={{
        ...buildFlipToggleButtonStyle(premiumSideAccent),
        "--chart-flip-toggle-hover-bg": withAlpha(premiumSideAccent, isLightTheme ? 0.92 : 0.9),
        "--chart-flip-toggle-focus-border": withAlpha(
          premiumSideAccent,
          isLightTheme ? 0.42 : 0.52,
        ),
        "--chart-flip-toggle-focus-ring": withAlpha(premiumSideAccent, isLightTheme ? 0.16 : 0.24),
      }}
      aria-pressed={premiumSideIsBuy}
      title={
        premiumSideIsBuy
          ? "Buy Premium. Tap to switch to Sell Premium"
          : "Sell Premium. Tap to switch to Buy Premium"
      }
      aria-label={
        premiumSideIsBuy
          ? "Buy Premium. Tap to switch to Sell Premium"
          : "Sell Premium. Tap to switch to Buy Premium"
      }
    >
      <PremiumSideIcon className="chart-flip-toggle-icon" size={15} />
      <span>{premiumSideLabel}</span>
    </button>
  );
  const topRightToggleControls = (
    <div
      ref={modeToggleRef}
      className="chart-flip-toggle-group"
      style={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "stretch",
        gap: 0,
      }}
    >
      {modeToggle}
      {premiumSideToggle}
    </div>
  );
  const toolbarButtonsContent = (() => {
    const groups = [];
    let lastGroup = null;
    chartSideToolbarButtons.forEach((button) => {
      if (button.floatingGroup === "chart-bottom") return;
      if (button.group !== lastGroup) {
        groups.push([]);
        lastGroup = button.group;
      }
      groups[groups.length - 1].push(button);
    });

    const separatorStyle = {
      width: "1px",
      height: "20px",
      background: CHART_COLORS.borderStrong,
      opacity: 0.5,
      flexShrink: 0,
      alignSelf: "center",
    };

    const elements = [];
    groups.forEach((group, gi) => {
      if (gi > 0) {
        elements.push(
          <span
            key={`sep-${gi}`}
            className="chart-toolbar-sep"
            style={separatorStyle}
            aria-hidden="true"
          />,
        );
      }
      group.forEach((button) => {
        const showSelectedStyle = button.active;
        const showOrdersChipStyle = button.key === "orders" && button.active;
        const isIconOnlyToolbar = isMobileViewport || isCompactToolbarViewport;
        const isDesktopInlineToolbar = !isIconOnlyToolbar;
        const defaultBg = "transparent";
        const defaultBorder = "transparent";
        const defaultColor = showOrdersChipStyle
          ? button.textColor || CHART_COLORS.fg
          : showSelectedStyle
            ? CHART_COLORS.fg
            : CHART_COLORS.mutedLight;
        const defaultHoverBg = withAlpha(CHART_COLORS.fg, 0.04);
        const defaultHoverBorder = "transparent";
        const defaultHoverText = showOrdersChipStyle
          ? button.hoverText || CHART_COLORS.fg
          : CHART_COLORS.fg;

        if (!isIconOnlyToolbar && button.key === "orders") {
          elements.push(
            <span
              key="sep-inline-orders"
              className="chart-toolbar-sep"
              style={{
                width: "1px",
                height: "24px",
                background: CHART_COLORS.borderStrong,
                opacity: 0.9,
                margin: "0 6px 0 2px",
                flexShrink: 0,
                alignSelf: "center",
              }}
              aria-hidden="true"
            />,
          );
        }

        elements.push(
          <button
            key={button.key}
            type="button"
            onClick={button.onClick}
            className={`chart-toolbar-button${showSelectedStyle ? " chart-toolbar-button-active" : ""}${isDesktopInlineToolbar ? " chart-toolbar-button-inline" : ""}`}
            style={{
              ...toolbarBaseStyle,
              padding: isIconOnlyToolbar ? 0 : showSelectedStyle ? "0 16px" : "0 10px",
              width: isIconOnlyToolbar ? (isMobileViewport ? "42px" : "38px") : "auto",
              minWidth: isIconOnlyToolbar
                ? isMobileViewport
                  ? "42px"
                  : "38px"
                : showSelectedStyle
                  ? "auto"
                  : "36px",
              height: isIconOnlyToolbar ? (isMobileViewport ? "42px" : "38px") : "32px",
              minHeight: isIconOnlyToolbar ? (isMobileViewport ? "42px" : "38px") : "32px",
              borderRadius: isIconOnlyToolbar ? "10px" : "14px",
              "--toolbar-button-bg": showOrdersChipStyle
                ? button.background || defaultBg
                : defaultBg,
              "--toolbar-button-border": showOrdersChipStyle
                ? button.borderColor || defaultBorder
                : defaultBorder,
              "--toolbar-button-color": defaultColor,
              "--toolbar-button-hover-bg": showOrdersChipStyle
                ? button.hoverBackground || defaultHoverBg
                : defaultHoverBg,
              "--toolbar-button-hover-border": showOrdersChipStyle
                ? button.hoverBorder || defaultHoverBorder
                : defaultHoverBorder,
              "--toolbar-button-hover-text": defaultHoverText,
              "--toolbar-button-active-bg": showOrdersChipStyle
                ? button.activeBackground || button.hoverBackground || defaultHoverBg
                : defaultHoverBg,
              "--toolbar-button-active-border": showOrdersChipStyle
                ? button.activeBorder || button.hoverBorder || defaultHoverBorder
                : defaultHoverBorder,
              "--toolbar-button-active-text": showOrdersChipStyle
                ? button.activeText || button.hoverText || defaultHoverText
                : defaultHoverText,
              fontSize: isDesktopInlineToolbar ? "11px" : "11px",
              fontWeight: 600,
              flexShrink: 0,
            }}
            title={button.title}
            aria-label={button.title}
          >
            {button.icon}
            {!isIconOnlyToolbar && <span>{button.label}</span>}
          </button>,
        );
      });
    });
    return elements;
  })();
  const chartBottomControls = chartSideToolbarButtons.filter(
    (button) => button.floatingGroup === "chart-bottom",
  );

  return (
    <div
      ref={fullscreenRootRef}
      tabIndex={-1}
      style={{
        background: CHART_COLORS.card,
        borderRadius: "12px",
        border: `1px solid ${CHART_COLORS.border}`,
        overflowX: "hidden",
        overflowY: isFullscreen ? "auto" : "hidden",
        position: "relative",
        ...(isFullscreen
          ? {
              position: "fixed",
              top: `${fullscreenFrame?.top ?? 0}px`,
              left: `${fullscreenFrame?.left ?? 0}px`,
              width: fullscreenOverlayWidth > 0 ? `${fullscreenOverlayWidth}px` : "100vw",
              height: fullscreenViewportHeight > 0 ? `${fullscreenViewportHeight}px` : "100dvh",
              zIndex: 1300,
              borderRadius: fullscreenFrame?.borderRadius || (isMobileViewport ? "0px" : "12px"),
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
            }
          : {}),
      }}
    >
      {!isMobileViewport && (
        <div
          style={{
            position: "absolute",
            top: "14px",
            right: "16px",
            zIndex: 30,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {oldSymbolSelector}
          {topRightToggleControls}
        </div>
      )}

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }

          @keyframes chartSpinnerSpin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }

          .chart-toolbar-button {
            box-shadow: inset 0 1px 0 var(--chart-toolbar-inset);
          }

          .chart-desktop-toolbar-row::-webkit-scrollbar {
            display: none;
          }

          ${
            isCompactToolbarViewport
              ? `
          .chart-toolbar-button {
            flex-direction: row;
            gap: 0 !important;
            box-shadow: none;
            line-height: 1;
          }

          .chart-toolbar-button .lucide {
            width: ${isMobileViewport ? "20px" : "18px"};
            height: ${isMobileViewport ? "20px" : "18px"};
          }
          `
              : ""
          }

          .chart-toolbar-button-icon {
            width: 34px;
            min-width: 34px;
            padding: 0;
            justify-content: center;
          }

          .chart-toolbar-button:hover {
            background: var(--toolbar-button-hover-bg, var(--chart-toolbar-hover-bg, ${CHART_COLORS.surface})) !important;
            border-color: var(--toolbar-button-hover-border, var(--chart-toolbar-hover-border, ${CHART_COLORS.borderStrong})) !important;
            color: var(--toolbar-button-hover-text, var(--chart-toolbar-hover-text, ${CHART_COLORS.fg})) !important;
          }

          .chart-toolbar-button:active {
            background: var(--toolbar-button-active-bg, var(--toolbar-button-hover-bg, var(--chart-toolbar-hover-bg, ${CHART_COLORS.surface}))) !important;
            border-color: var(--toolbar-button-active-border, var(--toolbar-button-hover-border, var(--chart-toolbar-hover-border, ${CHART_COLORS.borderStrong}))) !important;
            color: var(--toolbar-button-active-text, var(--toolbar-button-hover-text, var(--chart-toolbar-hover-text, ${CHART_COLORS.fg}))) !important;
          }

          .chart-toolbar-button-active {
            --toolbar-button-color: ${CHART_COLORS.fg};
          }

          .chart-toolbar-button-inline {
            box-shadow: none;
            background: var(--toolbar-button-bg, transparent) !important;
            border-color: var(--toolbar-button-border, transparent) !important;
          }

          .chart-toolbar-button-inline:hover {
            background: var(--toolbar-button-hover-bg, transparent) !important;
            border-color: var(--toolbar-button-hover-border, transparent) !important;
          }

          .chart-toolbar-button-inline .lucide {
            width: 17px;
            height: 17px;
          }

          .chart-side-toolbar-button:hover {
            --chart-side-button-bg: var(--chart-toolbar-hover-bg, ${CHART_COLORS.surface});
            --chart-side-button-color: ${CHART_COLORS.fg};
          }

          .chart-side-toolbar-button-active {
            --chart-side-button-color: ${CHART_COLORS.fg};
          }

          .chart-flip-toggle-group {
            border-radius: 11px;
            overflow: hidden;
          }

          .chart-flip-toggle-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            white-space: nowrap;
            font-size: ${isMobileViewport ? "12px" : "11px"};
            line-height: 1;
          }

          .chart-flip-toggle-button + .chart-flip-toggle-button {
            border-left: 1px solid ${withAlpha(CHART_COLORS.fg, isLightTheme ? 0.08 : 0.12)};
          }

          .chart-flip-toggle-button:hover {
            background: var(--chart-flip-toggle-hover-bg, ${CHART_COLORS.surface});
            color: inherit;
          }

          .chart-flip-toggle-button:focus-visible {
            outline: none;
            box-shadow: inset 0 1px 0 ${withAlpha(CHART_COLORS.fg, isLightTheme ? 0.15 : 0.18)}, 0 0 0 1px var(--chart-flip-toggle-focus-border, ${CHART_COLORS.blueLight}), 0 0 0 3px var(--chart-flip-toggle-focus-ring, ${withAlpha(CHART_COLORS.blueLight, 0.22)});
          }

          .chart-flip-toggle-button:active {
            background: var(--chart-flip-toggle-hover-bg, ${CHART_COLORS.surface});
          }

          .chart-flip-toggle-icon {
            flex-shrink: 0;
            width: 15px;
            height: 15px;
          }

          ${
            isMobileViewport
              ? `
          .chart-flip-toggle-icon {
            display: none;
          }
          `
              : ""
          }

          .chart-bottom-controls {
            position: absolute;
            left: 50%;
            bottom: 48px;
            transform: translateX(-50%);
            z-index: 26;
            display: inline-flex;
            align-items: center;
            gap: 0;
            border-radius: 10px;
            background: ${CHART_COLORS.card};
            border: 1px solid ${CHART_COLORS.borderStrong};
            backdrop-filter: none;
            overflow: hidden;
          }

          .chart-bottom-control-btn {
            width: 38px;
            min-width: 38px;
            height: 34px;
            padding: 0;
            border: none;
            border-right: 1px solid ${CHART_COLORS.borderStrong};
            border-radius: 0;
            background: ${CHART_COLORS.surface};
            color: ${CHART_COLORS.mutedLight};
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: none;
            transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
          }

          .chart-bottom-control-btn:first-child {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
          }

          .chart-bottom-control-btn:last-child {
            border-right: none;
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
          }

          .chart-bottom-control-btn:hover {
            color: ${CHART_COLORS.fg};
            background: ${CHART_COLORS.input};
          }

          .chart-bottom-control-btn:active {
            color: ${CHART_COLORS.fg};
            background: ${CHART_COLORS.input};
          }

          .chart-toolbar-select {
            --cs-height: 42px;
            --cs-radius: 9px;
            --cs-padding-x: 14px;
            --cs-option-height: ${isMobileViewport ? "34" : "36"};
            --cs-font-size: 11px;
          }

          .chart-toolbar-select .cs-value {
            display: block;
            width: 100%;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            word-break: normal;
            overflow-wrap: normal;
          }

          .chart-toolbar-select .cs-trigger {
            min-width: 0;
            font-size: 11px;
          }

          .chart-toolbar-select-dropdown .cs-option,
          .chart-toolbar-select-dropdown .cs-empty {
            font-size: 11px;
          }

          .chart-toolbar-select-dropdown .cs-option {
            display: block;
            width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            word-break: normal;
            overflow-wrap: normal;
          }

          .replay-speed-pill {
            transition: background 0.18s ease, color 0.18s ease;
          }

          .replay-speed-pill[data-active="false"]:hover {
            color: ${CHART_COLORS.blueLight} !important;
          }

          .replay-slider {
            position: relative;
            display: flex;
            align-items: center;
            height: 22px;
            width: 100%;
          }

          .replay-slider-track {
            position: absolute;
            left: 0;
            right: 0;
            height: 8px;
            border-radius: 999px;
            background: ${withAlpha(CHART_COLORS.borderStrong, 0.55)};
            overflow: hidden;
          }

          .replay-slider-progress {
            position: absolute;
            inset: 0 auto 0 0;
            height: 100%;
            border-radius: 999px;
            background: linear-gradient(90deg, ${CHART_COLORS.blue}, ${CHART_COLORS.blueLight});
          }

          .replay-slider-thumb {
            position: absolute;
            top: 50%;
            width: 18px;
            height: 18px;
            border-radius: 999px;
            background: ${CHART_COLORS.blue};
            border: none;
            box-shadow: none;
            transform: translate(-50%, -50%);
            pointer-events: none;
          }

          .replay-slider-input {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            appearance: none;
            -webkit-appearance: none;
            background: transparent;
            opacity: 0;
            cursor: pointer;
            margin: 0;
          }
        `}
      </style>
      <div
        style={{
          padding: "14px 16px 12px",
          paddingRight: isMobileViewport ? "16px" : isOldContractsMode ? "496px" : "296px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minWidth: 0,
            flex: "1 1 420px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            {isMobileViewport ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "start",
                  gap: "8px 12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: "0.98rem",
                    fontWeight: 600,
                    color: CHART_COLORS.fg,
                    minWidth: 0,
                  }}
                >
                  <ChartCandlestick
                    size={16}
                    color={CHART_COLORS.mutedLight}
                    style={{ flexShrink: 0 }}
                  />
                  <span>Premium Chart</span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "20px",
                      padding: "0 8px",
                      borderRadius: "999px",
                      border: `1px solid ${chartStatusBadge.border}`,
                      background: chartStatusBadge.background,
                      color: chartStatusBadge.color,
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      flexShrink: 0,
                    }}
                  >
                    {chartStatusBadge.label}
                  </span>
                </div>
                <div style={{ justifySelf: "end" }}>{topRightToggleControls}</div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.84rem",
                    lineHeight: 1.55,
                    color: CHART_COLORS.mutedLight,
                  }}
                >
                  Professional premium view with live ticks, indicators, and export tools
                </p>
                <div style={{ justifySelf: "end" }}>{oldSymbolSelector}</div>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.3rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.98rem",
                      fontWeight: 600,
                      color: CHART_COLORS.fg,
                    }}
                  >
                    <ChartCandlestick
                      size={16}
                      color={CHART_COLORS.mutedLight}
                      style={{ flexShrink: 0 }}
                    />
                    <span>Premium Chart</span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "20px",
                        padding: "0 8px",
                        borderRadius: "999px",
                        border: `1px solid ${chartStatusBadge.border}`,
                        background: chartStatusBadge.background,
                        color: chartStatusBadge.color,
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      {chartStatusBadge.label}
                    </span>
                  </div>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.84rem",
                    lineHeight: 1.55,
                    color: CHART_COLORS.mutedLight,
                  }}
                >
                  Professional premium view with live ticks, indicators, and export tools
                </p>
              </>
            )}
            {!isMobileViewport && (
              <div
                className="chart-desktop-toolbar-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "8px",
                  flexWrap: "nowrap",
                  marginTop: "10px",
                  minWidth: 0,
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                }}
              >
                {toolbarButtonsContent}
              </div>
            )}
          </div>

          {indicatorPanelOpen && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobileViewport ? "10px" : "18px",
                background: CHART_COLORS.overlay,
                backdropFilter: "blur(4px)",
              }}
              onClick={() => setIndicatorPanelOpen(false)}
            >
              <div
                ref={indicatorPanelRef}
                style={{
                  width: isMobileViewport ? "90vw" : "min(100%, 820px)",
                  maxWidth: isMobileViewport ? "90vw" : "820px",
                  maxHeight: isMobileViewport ? "90vh" : "calc(100vh - 36px)",
                  overflowY: "auto",
                  background: CHART_COLORS.card,
                  border: `1px solid ${CHART_COLORS.border}`,
                  borderRadius: "12px",
                  padding: isMobileViewport ? "12px" : "14px",
                  boxShadow: CHART_COLORS.panelShadow,
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "10px",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                    <div
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "9px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1px solid ${CHART_COLORS.border}`,
                        background: CHART_COLORS.surface,
                        color: CHART_COLORS.mutedLight,
                        flexShrink: 0,
                      }}
                    >
                      <Layers3 size={16} />
                    </div>
                    <div>
                      <div
                        style={{
                          color: CHART_COLORS.fg,
                          fontSize: "0.96rem",
                          fontWeight: 600,
                          marginBottom: "0.25rem",
                        }}
                      >
                        Indicator Library
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.8rem",
                          lineHeight: 1.5,
                          color: CHART_COLORS.mutedLight,
                        }}
                      >
                        Configure overlays and momentum tools using the same chart theme as the rest
                        of the workspace.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIndicatorPanelOpen(false)}
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: 0,
                      border: `1px solid ${CHART_COLORS.border}`,
                      borderRadius: "8px",
                      background: "transparent",
                      color: CHART_COLORS.mutedLight,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                    aria-label="Close indicators panel"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompactViewport ? "1fr" : "minmax(0, 1fr) 250px",
                    gap: isMobileViewport ? "12px" : "16px",
                    minHeight: isCompactViewport ? "auto" : "420px",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        marginBottom: "8px",
                        color: CHART_COLORS.mutedLight,
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      Script Name
                    </div>

                    <div
                      style={{
                        maxHeight: isCompactViewport ? "none" : "calc(100vh - 245px)",
                        overflowY: isCompactViewport ? "visible" : "auto",
                        paddingRight: isCompactViewport ? 0 : "4px",
                      }}
                    >
                      {filteredLibraryItems.length === 0 ? (
                        <div
                          style={{
                            color: CHART_COLORS.mutedLight,
                            fontSize: "13px",
                            paddingTop: "10px",
                          }}
                        >
                          No indicators found for this search.
                        </div>
                      ) : (
                        filteredLibraryItems.map((item) => {
                          const active = item.key === selectedIndicatorKey;
                          const enabled = indicatorSettings[item.key]?.enabled;
                          const accent = CHART_COLORS[item.accentKey] || CHART_COLORS.blueLight;

                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => setSelectedIndicatorKey(item.key)}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "none",
                                borderRadius: "10px",
                                background: active ? CHART_COLORS.surface : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                                cursor: "pointer",
                                textAlign: "left",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    color: CHART_COLORS.fg,
                                    fontSize: "14px",
                                    fontWeight: active ? 600 : 500,
                                    marginBottom: "2px",
                                  }}
                                >
                                  {item.title}
                                </div>
                                <div
                                  style={{
                                    color: CHART_COLORS.mutedLight,
                                    fontSize: "11px",
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {item.description}
                                </div>
                              </div>
                              <div
                                style={{
                                  minWidth: "54px",
                                  minHeight: "24px",
                                  padding: "0 8px",
                                  borderRadius: "999px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: enabled
                                    ? withAlpha(accent, 0.16)
                                    : CHART_COLORS.input,
                                  color: enabled ? accent : CHART_COLORS.mutedLight,
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  letterSpacing: "0.06em",
                                  textTransform: "uppercase",
                                }}
                              >
                                {enabled ? "Added" : "Add"}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      borderLeft: isCompactViewport ? "none" : `1px solid ${CHART_COLORS.border}`,
                      borderTop: isCompactViewport ? `1px solid ${CHART_COLORS.border}` : "none",
                      paddingLeft: isCompactViewport ? 0 : "14px",
                      paddingTop: isCompactViewport ? "12px" : 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: CHART_COLORS.fg,
                          fontSize: "16px",
                          fontWeight: 600,
                          marginBottom: "4px",
                        }}
                      >
                        {selectedIndicatorMeta.title}
                      </div>
                      <div
                        style={{
                          color: CHART_COLORS.mutedLight,
                          fontSize: "12px",
                          lineHeight: 1.55,
                        }}
                      >
                        {selectedIndicatorMeta.description}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleIndicator(selectedIndicatorMeta.key)}
                      style={{
                        minHeight: "36px",
                        padding: "0 12px",
                        borderRadius: "10px",
                        border: `1px solid ${selectedIndicatorConfig?.enabled ? withAlpha(selectedIndicatorAccent, 0.4) : CHART_COLORS.border}`,
                        background: selectedIndicatorConfig?.enabled
                          ? withAlpha(selectedIndicatorAccent, 0.14)
                          : CHART_COLORS.surface,
                        color: CHART_COLORS.fg,
                        fontSize: "12px",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        alignSelf: "flex-start",
                      }}
                    >
                      {selectedIndicatorConfig?.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                      {selectedIndicatorConfig?.enabled ? "Added to Chart" : "Add to Chart"}
                    </button>

                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "999px",
                        background: selectedIndicatorAccent,
                        boxShadow: `0 0 0 3px ${withAlpha(selectedIndicatorAccent, 0.12)}`,
                      }}
                    />

                    {selectedIndicatorFields.length > 0 ? (
                      <div style={{ display: "grid", gap: "10px" }}>
                        {selectedIndicatorFields.map((field) => (
                          <label key={field.label} style={{ display: "grid", gap: "5px" }}>
                            <span
                              style={{
                                color: CHART_COLORS.mutedLight,
                                fontSize: "10px",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                              }}
                            >
                              {field.label}
                            </span>
                            <input
                              type="number"
                              value={field.value}
                              onChange={(event) => field.onChange(event.target.value)}
                              onBlur={field.onBlur}
                              style={{
                                ...indicatorInputStyle,
                                width: "100%",
                                minWidth: 0,
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          color: CHART_COLORS.mutedLight,
                          fontSize: "12px",
                          lineHeight: 1.6,
                        }}
                      >
                        This indicator uses session defaults and does not require extra parameter
                        inputs.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {isMobileViewport && (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              flexWrap: "wrap",
              marginTop: "2px",
            }}
          >
            {toolbarButtonsContent}
          </div>
        )}
      </div>

      <div
        style={{
          background: CHART_COLORS.card,
          padding: "8px 16px",
          fontSize: "12px",
          color: CHART_COLORS.mutedLight,
          borderBottom: `1px solid ${CHART_COLORS.border}`,
          display: "flex",
          alignItems: isMobileViewport ? "center" : "stretch",
          gap: "16px",
          flexWrap: "wrap",
          justifyContent: isMobileViewport ? "center" : "space-between",
          textAlign: isMobileViewport ? "center" : "left",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            justifyContent: isMobileViewport ? "center" : "flex-start",
            width: isMobileViewport ? "100%" : "auto",
          }}
        >
          <span style={metricLabelStyle}>
            <BarChart3 size={13} />{" "}
            <span style={{ color: CHART_COLORS.fg }}>{candleCountLabel}</span>
          </span>
          {isOldContractsMode ? (
            <>
              <span style={metricLabelStyle}>
                <ChartCandlestick size={13} />{" "}
                <span style={{ color: CHART_COLORS.fg }}>{selectedOldSymbol || "—"}</span>
              </span>
              <span style={metricLabelStyle}>
                <Clock3 size={13} />{" "}
                <span style={{ color: CHART_COLORS.fg }}>{getTimeframeLabel(tf)}</span>
              </span>
            </>
          ) : (
            <>
              <span style={metricLabelStyle}>
                <Clock3 size={13} /> <span style={{ color: CHART_COLORS.fg }}>{tf}M</span>
              </span>
              <span style={metricLabelStyle}>
                <Building2 size={13} />{" "}
                <span style={{ color: CHART_COLORS.fg }}>{liveSymbolPairLabel || "—"}</span>
              </span>
            </>
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: isMobileViewport ? "center" : "flex-start",
            width: isMobileViewport ? "100%" : "auto",
          }}
        >
          {!isOldContractsMode && (
            <span style={metricLabelStyle}>
              <User size={13} />{" "}
              <span style={{ color: CHART_COLORS.fg }}>#{accountSetId?.slice(-6)}</span>
            </span>
          )}
          {displayCandle && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                justifyContent: isMobileViewport ? "center" : "flex-start",
              }}
            >
              {ohlcReadoutItems.map((item) => (
                <span
                  key={item.label}
                  style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                >
                  <span style={{ color: CHART_COLORS.mutedLight }}>{item.label}</span>
                  <span style={{ color: CHART_COLORS.fg, fontVariantNumeric: "tabular-nums" }}>
                    {formatPremiumValue(item.value)}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {replayActive && candles.length > 1 && (
        <div
          style={{
            padding: isMobileViewport ? "10px 12px" : "10px 16px",
            borderBottom: `1px solid ${CHART_COLORS.border}`,
            background: CHART_COLORS.card,
            display: "flex",
            alignItems: isMobileViewport ? "stretch" : "center",
            justifyContent: "space-between",
            gap: isMobileViewport ? "10px" : "14px",
            flexWrap: isMobileViewport ? "wrap" : "nowrap",
            overflowX: isMobileViewport ? "hidden" : "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0",
              flexShrink: 0,
              borderRadius: "12px",
              border: `1px solid ${CHART_COLORS.overlayBorder}`,
              background: CHART_COLORS.surface,
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              onClick={toggleReplayPlayback}
              style={{
                width: "42px",
                minWidth: "42px",
                height: "38px",
                minHeight: "38px",
                padding: 0,
                borderRadius: 0,
                border: "none",
                borderRight: `1px solid ${CHART_COLORS.overlayBorder}`,
                background: "transparent",
                color: CHART_COLORS.mutedLight,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "none",
                transition: "background 0.15s ease, color 0.15s ease",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = CHART_COLORS.blueLight;
                event.currentTarget.style.color = "#ffffff";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "transparent";
                event.currentTarget.style.color = CHART_COLORS.mutedLight;
              }}
              aria-label={replayPlaying ? "Pause replay" : "Play replay"}
              title={replayPlaying ? "Pause replay" : "Play replay"}
            >
              {replayPlaying ? <Pause size={15} /> : <Play size={15} />}
            </button>
            <button
              type="button"
              onClick={restartReplay}
              style={{
                width: "42px",
                minWidth: "42px",
                height: "38px",
                minHeight: "38px",
                padding: 0,
                borderRadius: 0,
                border: "none",
                borderRight: `1px solid ${CHART_COLORS.overlayBorder}`,
                background: "transparent",
                color: CHART_COLORS.mutedLight,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "none",
                transition: "background 0.15s ease, color 0.15s ease",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = CHART_COLORS.emerald;
                event.currentTarget.style.color = "#ffffff";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "transparent";
                event.currentTarget.style.color = CHART_COLORS.mutedLight;
              }}
              aria-label="Restart replay"
              title="Restart replay"
            >
              <RotateCcw size={15} />
            </button>
            <button
              type="button"
              onClick={exitReplay}
              style={{
                width: "42px",
                minWidth: "42px",
                height: "38px",
                minHeight: "38px",
                padding: 0,
                borderRadius: 0,
                border: "none",
                background: "transparent",
                color: CHART_COLORS.mutedLight,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "none",
                transition: "background 0.15s ease, color 0.15s ease",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = CHART_COLORS.rose;
                event.currentTarget.style.color = "#ffffff";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "transparent";
                event.currentTarget.style.color = CHART_COLORS.mutedLight;
              }}
              aria-label="Exit replay"
              title="Exit replay"
            >
              <X size={15} />
            </button>
          </div>

          <div
            style={{
              minWidth: isMobileViewport ? "100%" : "min(100%, 640px)",
              flex: "1 1 480px",
              display: "flex",
              flexDirection: isMobileViewport ? "column" : "row",
              alignItems: isMobileViewport ? "stretch" : "center",
              gap: isMobileViewport ? "4px" : "14px",
              marginLeft: isMobileViewport ? "0" : "16px",
              marginRight: isMobileViewport ? "0" : "8px",
              order: isMobileViewport ? 3 : 0,
            }}
          >
            {isMobileViewport && (
              <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span
                  style={{
                    color: CHART_COLORS.mutedLight,
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                >
                  {replayPlaying ? "Playing" : "Paused"}
                </span>
                <span
                  style={{
                    color: CHART_COLORS.mutedLight,
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                >
                  {replayHistoricalCandleCount}/{historicalCandleCount} ({replayProgress}%)
                </span>
              </div>
            )}
            {!isMobileViewport && (
              <span
                style={{
                  color: CHART_COLORS.mutedLight,
                  fontWeight: 600,
                  fontSize: "12px",
                  flexShrink: 0,
                }}
              >
                {replayPlaying ? "Playing" : "Paused"}
              </span>
            )}
            <div className="replay-slider">
              <div className="replay-slider-track">
                <div
                  className="replay-slider-progress"
                  style={{ width: `${replayProgressRatio}%` }}
                />
              </div>
              <div className="replay-slider-thumb" style={{ left: `${replayProgressRatio}%` }} />
              <input
                className="replay-slider-input"
                type="range"
                min="1"
                max={String(Math.max(candles.length, 1))}
                value={String(replayVisibleCount)}
                onChange={(event) => handleReplayScrub(event.target.value)}
                aria-label="Replay position"
              />
            </div>
            {!isMobileViewport && (
              <span
                style={{
                  color: CHART_COLORS.mutedLight,
                  fontWeight: 600,
                  fontSize: "12px",
                  flexShrink: 0,
                }}
              >
                {replayHistoricalCandleCount}/{historicalCandleCount} ({replayProgress}%)
              </span>
            )}
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0",
              flexShrink: 0,
              padding: "4px",
              borderRadius: "18px",
              border: `1px solid ${CHART_COLORS.overlayBorder}`,
              background: CHART_COLORS.surface,
            }}
          >
            {REPLAY_SPEED_OPTIONS.map((speed) => {
              const isActiveSpeed = replaySpeed === speed;

              return (
                <button
                  key={speed}
                  type="button"
                  onClick={() => setReplaySpeed(speed)}
                  className="replay-speed-pill"
                  data-active={isActiveSpeed ? "true" : "false"}
                  style={{
                    minWidth: "44px",
                    height: "28px",
                    padding: "0 10px",
                    borderRadius: "14px",
                    border: "none",
                    background: isActiveSpeed ? "var(--admin-blue, #2563eb)" : "transparent",
                    color: isActiveSpeed ? "#ffffff" : CHART_COLORS.mutedLight,
                    fontSize: "11px",
                    fontWeight: isActiveSpeed ? 700 : 600,
                    cursor: "pointer",
                    boxShadow: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    letterSpacing: "0.01em",
                    textAlign: "center",
                  }}
                  title={`Replay speed ${speed}x`}
                >
                  {speed}x
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: CHART_COLORS.fg,
            fontSize: "16px",
          }}
        >
          Loading chart data...
        </div>
      )}

      <div
        style={{ position: "relative", height: chartPrimaryHeight, background: CHART_COLORS.card }}
      >
        <div
          style={{ position: "relative", height: "100%", minWidth: 0 }}
          onMouseEnter={isMobileViewport ? undefined : () => showChartControls()}
          onMouseMove={isMobileViewport ? undefined : () => showChartControls()}
          onMouseLeave={isMobileViewport ? undefined : hideChartControls}
          onTouchStart={() => showChartControls({ autoHideMs: CHART_CONTROLS_MOBILE_HIDE_DELAY })}
          onTouchMove={() => showChartControls({ autoHideMs: CHART_CONTROLS_MOBILE_HIDE_DELAY })}
        >
          {!loading && chartControlsVisible && chartBottomControls.length > 0 && (
            <div className="chart-bottom-controls">
              {chartBottomControls.map((button) => (
                <button
                  key={button.key}
                  type="button"
                  onClick={button.onClick}
                  className="chart-bottom-control-btn"
                  title={button.title}
                  aria-label={button.title}
                >
                  {button.icon}
                </button>
              ))}
            </div>
          )}
          <div
            ref={chartContainer}
            style={{
              width: "100%",
              height: "100%",
              background: CHART_COLORS.card,
            }}
          />
          {showChartOverlayLoader && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: CHART_COLORS.card,
                backdropFilter: "none",
                zIndex: 20,
                pointerEvents: "none",
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "999px",
                  borderTop: `3px solid ${CHART_COLORS.blue}`,
                  borderRight: `3px solid ${CHART_COLORS.muted}`,
                  borderBottom: `3px solid ${CHART_COLORS.muted}`,
                  borderLeft: `3px solid ${CHART_COLORS.muted}`,
                  animation: "chartSpinnerSpin 0.85s linear infinite",
                }}
              />
            </div>
          )}
          {!loading && displayCandle && (
            <div
              style={{
                position: "absolute",
                top: "12px",
                left: "12px",
                padding: "8px 10px",
                borderRadius: "8px",
                background: CHART_COLORS.overlay,
                border: `1px solid ${CHART_COLORS.overlayBorder}`,
                color: CHART_COLORS.fgDim,
                fontSize: "11px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                pointerEvents: "none",
                backdropFilter: "blur(6px)",
              }}
            >
              <div style={{ fontWeight: "600", color: CHART_COLORS.blueLight }}>
                {formatTimeLabel(displayTimeKey)}
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {ohlcReadoutItems.map((item) => (
                  <span
                    key={item.label}
                    style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
                  >
                    <span style={{ color: CHART_COLORS.mutedLight }}>{item.label}</span>
                    <span
                      style={{
                        color: getOhlcValueColor(displayCandle.open, item.value, item.label),
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatPremiumValue(item.value)}
                    </span>
                  </span>
                ))}
              </div>
              {indicatorReadouts.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
                    gap: "4px 8px",
                  }}
                >
                  {indicatorReadouts.map((item) => (
                    <span key={item.label}>
                      {item.label}:{" "}
                      {item.value === null || item.value === undefined
                        ? "--"
                        : Number(item.value).toFixed(5)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {indicatorSettings.rsi.enabled && (
        <div
          style={{
            background: CHART_COLORS.card,
            borderTop: `1px solid ${CHART_COLORS.border}`,
            position: "relative",
          }}
        >
          <div style={subPanelLabelStyle}>
            <span style={{ color: indicatorSettings.rsi.color, fontWeight: "600" }}>RSI</span>
            <span>Period: {indicatorSettings.rsi.period}</span>
          </div>
          <div
            ref={rsiContainerRef}
            style={{
              width: "100%",
              height: "140px",
            }}
          />
        </div>
      )}

      {indicatorSettings.macd.enabled && (
        <div
          style={{
            background: CHART_COLORS.card,
            borderTop: `1px solid ${CHART_COLORS.border}`,
            position: "relative",
          }}
        >
          <div style={subPanelLabelStyle}>
            <span style={{ color: indicatorSettings.macd.color, fontWeight: "600" }}>MACD</span>
            <span>Fast: {indicatorSettings.macd.fast}</span>
            <span>Slow: {indicatorSettings.macd.slow}</span>
            <span>Signal: {indicatorSettings.macd.signal}</span>
          </div>
          <div
            ref={macdContainerRef}
            style={{
              width: "100%",
              height: "160px",
            }}
          />
        </div>
      )}

      {indicatorSettings.atr.enabled && (
        <div
          style={{
            background: CHART_COLORS.card,
            borderTop: `1px solid ${CHART_COLORS.border}`,
            position: "relative",
          }}
        >
          <div style={subPanelLabelStyle}>
            <span style={{ color: indicatorSettings.atr.color, fontWeight: "600" }}>ATR</span>
            <span>Period: {indicatorSettings.atr.period}</span>
          </div>
          <div
            ref={atrContainerRef}
            style={{
              width: "100%",
              height: "140px",
            }}
          />
        </div>
      )}

      {indicatorSettings.stoch.enabled && (
        <div
          style={{
            background: CHART_COLORS.card,
            borderTop: `1px solid ${CHART_COLORS.border}`,
            position: "relative",
          }}
        >
          <div style={subPanelLabelStyle}>
            <span style={{ color: indicatorSettings.stoch.colorK, fontWeight: "600" }}>
              Stochastic
            </span>
            <span>K: {indicatorSettings.stoch.kPeriod}</span>
            <span>D: {indicatorSettings.stoch.dPeriod}</span>
          </div>
          <div
            ref={stochContainerRef}
            style={{
              width: "100%",
              height: "150px",
            }}
          />
        </div>
      )}

      {!isMobileViewport && !showChartOverlayLoader && !error && candles.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: CHART_COLORS.mutedLight,
          }}
        >
          <BarChart3 size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
          <h3 style={{ color: CHART_COLORS.fg, margin: "0 0 8px 0" }}>No Data Available</h3>
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Premium data collection may not be active.
            <br />
            Please ensure symbols are locked and trading is active.
          </p>
        </div>
      )}

      <div
        style={{
          padding: isMobileViewport ? "8px 12px 10px" : "6px 12px",
          borderTop: `1px solid ${CHART_COLORS.border}`,
          background: CHART_COLORS.card,
          color: CHART_COLORS.mutedLight,
          fontSize: "11px",
          display: "flex",
          alignItems: isMobileViewport ? "stretch" : "center",
          justifyContent: isMobileViewport ? "center" : "space-between",
          gap: isMobileViewport ? "8px" : "14px",
          flexWrap: "wrap",
          flexDirection: isMobileViewport ? "column" : "row",
          textAlign: isMobileViewport ? "center" : "left",
        }}
      >
        <div
          style={{
            width: isMobileViewport ? "100%" : "auto",
            overflowX: isMobileViewport ? "auto" : "visible",
            overflowY: "hidden",
            WebkitOverflowScrolling: isMobileViewport ? "touch" : "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: isMobileViewport ? "center" : "flex-start",
            gap: isMobileViewport ? "4px" : "2px",
            padding: isMobileViewport ? "2px 0" : "2px",
            borderRadius: "10px",
            background: "transparent",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: isMobileViewport ? "4px" : "2px",
              minWidth: isMobileViewport ? "max-content" : "auto",
            }}
          >
            {footerTimeframeButtons.map((item) => (
              <button
                key={item.key}
                onClick={item.onClick}
                type="button"
                style={{
                  minWidth: isMobileViewport ? "42px" : "36px",
                  height: isMobileViewport ? "28px" : "26px",
                  padding: isMobileViewport ? "0 10px" : "0 9px",
                  border: item.active
                    ? `1px solid ${withAlpha(CHART_COLORS.blueLight, 0.34)}`
                    : "none",
                  borderRadius: "8px",
                  background: item.active ? withAlpha(CHART_COLORS.blue, 0.14) : "transparent",
                  color: item.active ? CHART_COLORS.blueLight : CHART_COLORS.mutedLight,
                  fontSize: "12px",
                  fontWeight: item.active ? 700 : 600,
                  cursor: "pointer",
                  letterSpacing: "0.01em",
                  boxShadow: "none",
                  transition: "background 0.18s ease, border-color 0.18s ease, color 0.18s ease",
                }}
                title={`Set timeframe to ${item.label}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: isMobileViewport ? "center" : "flex-start",
            gap: "12px",
            flexShrink: 0,
            minHeight: "28px",
            padding: isMobileViewport ? "0" : "0 2px",
            width: isMobileViewport ? "100%" : "auto",
          }}
        >
          <button
            type="button"
            onClick={() => setShowGrid((previous) => !previous)}
            title={showGrid ? "Hide chart grid" : "Show chart grid"}
            aria-label={showGrid ? "Hide chart grid" : "Show chart grid"}
            aria-pressed={showGrid}
            style={{
              width: "26px",
              height: "26px",
              borderRadius: "8px",
              border: `1px solid ${showGrid ? withAlpha(CHART_COLORS.blueLight, 0.34) : CHART_COLORS.borderStrong}`,
              background: showGrid ? withAlpha(CHART_COLORS.blue, 0.14) : CHART_COLORS.surface,
              color: showGrid ? CHART_COLORS.blueLight : CHART_COLORS.mutedLight,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              boxShadow: "none",
              transition: "background 0.18s ease, border-color 0.18s ease, color 0.18s ease",
            }}
          >
            <Grid2x2 size={13} />
          </button>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              color: CHART_COLORS.fgDim,
              fontWeight: 500,
              lineHeight: 1,
              fontSize: "12px",
            }}
          >
            <Clock3 size={13} />
            {liveClockLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
